import { useState, useEffect, useRef, useMemo, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useSearchParams, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import BepariAakda from "./BepariAakda";
import SearchableSelect from "./SearchableSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// Auth token interceptor: attach Authorization header from localStorage if available
// This makes auth work even when 3rd-party cookies are blocked (cross-origin frontend↔backend)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============== AUTH CONTEXT ==============
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);     // null = checking, false = not logged in
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(res => {
      setUser(res.data);
      setChecking(false);
    }).catch(() => {
      localStorage.removeItem("auth_token");
      setUser(false);
      setChecking(false);
    });
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.access_token) {
      localStorage.setItem("auth_token", res.data.access_token);
    }
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem("auth_token");
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, checking, login, logout }}>{children}</AuthContext.Provider>;
};

const useAuth = () => useContext(AuthContext);

// ============== LOGIN PAGE ==============
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Mandi</h1>
          <span>Accounting App</span>
          <p className="login-firm">Haji Mushtaq Nana & Sons</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="login-error" data-testid="login-error">{error}</div>}
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
          <button type="submit" disabled={loading} data-testid="login-submit">{loading ? 'Signing in...' : 'Sign In'}</button>
        </form>
        <button
          type="button"
          data-testid="login-demo-btn"
          onClick={() => { setEmail('demo@mandipro.in'); setPassword('demo@2026'); }}
          style={{
            marginTop: '14px',
            width: '100%',
            background: 'transparent',
            color: '#d97706',
            border: '1px dashed #d97706',
            padding: '8px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >🧪 Try Demo Account</button>
      </div>
    </div>
  );
};

// ============== PROTECTED ROUTE ==============
const ProtectedRoute = ({ children }) => {
  const { user, checking } = useAuth();
  if (checking) return <div className="login-page"><div className="loading">Loading...</div></div>;
  if (user === false) return <Navigate to="/login" />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" />;
  return children;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
};

const fmtShort = (amount) => {
  if (!amount) return "₹0";
  const abs = Math.abs(amount);
  if (abs >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
  return formatCurrency(amount);
};

const fmtDate = (dateStr) => {
  if (!dateStr || dateStr === '-' || dateStr === 'Opening') return dateStr || '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

// ============== SIDEBAR ==============
const Sidebar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: "/", label: "Dashboard", icon: "📊" },
    { path: "/daily-sales", label: "Daily Sales", icon: "🐐" },
    { path: "/cash-book", label: "Cash & Bank", icon: "💰" },
    { path: "/adjustments", label: "Adjustments (JV)", icon: "🔄" },
    { path: "/balance-transfer", label: "Balance Transfer", icon: "↔️", hidden: true },
    { path: "/bepaari-ledger", label: "Bepaari Ledger", icon: "📒" },
    { path: "/dukandar-ledger", label: "Dukandar Ledger", icon: "📗" },
    { path: "/balance-sheet", label: "Balance Sheet", icon: "📑", adminOnly: true },
    { path: "/bepaari-aakda", label: "Bepaari Aakda", icon: "🧾" },
    { path: "/party-statement", label: "Party Statement", icon: "📄" },
    { path: "/masters", label: "Masters", icon: "⚙️", adminOnly: true },
    { path: "/activity-log", label: "Activity Log", icon: "📋", adminOnly: true },
  ];

  const isAdmin = user?.role === 'admin';
  const visibleNavItems = navItems.filter(item => !item.hidden && (!item.adminOnly || isAdmin));

  return (
    <>
      <button className="mobile-hamburger" data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
        {mobileOpen ? "✕" : "☰"}
      </button>
      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />}
      <aside className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`} data-testid="sidebar">
        <div className="sidebar-header">
          <h1>Mandi</h1>
          <span>Accounting App</span>
        </div>
        <nav className="sidebar-nav">
          {visibleNavItems.map((item) => (
            <NavLink key={item.path} to={item.path} data-testid={`nav-${item.path.replace(/\//g, '') || 'dashboard'}`} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={() => setMobileOpen(false)}>
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
          <div className="sidebar-user">
            <span className="sidebar-user-name">{user?.name || user?.email}</span>
            <button className="sidebar-logout" onClick={async () => { await logout(); navigate('/login'); }} data-testid="logout-btn">Logout</button>
          </div>
        </nav>
      </aside>
    </>
  );
};

// ============== DASHBOARD (Dalal Premium Traditional) ==============
const Dashboard = () => {
  const [data, setData] = useState(null);
  const [todaySales, setTodaySales] = useState([]);
  const [recentLabel, setRecentLabel] = useState("Today's");
  const [mtd, setMtd] = useState(null);
  const [topRecv, setTopRecv] = useState([]);
  const [topPay, setTopPay] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const monthStart = today.substring(0, 7) + '-01';
    const calls = [
      isAdmin ? axios.get(`${API}/balance-sheet`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      axios.get(`${API}/daily-sales?from_date=${today}&to_date=${today}`),
      axios.get(`${API}/daily-sales?from_date=${monthStart}&to_date=${today}`),
      axios.get(`${API}/dukandar-ledger`),
      axios.get(`${API}/bepaari-ledger`),
    ];
    Promise.all(calls).then(([bsRes, todayRes, mtdRes, dukRes, bepRes]) => {
      setData(bsRes.data);
      const ts = todayRes.data;
      if (ts.length > 0) {
        setTodaySales(ts.sort((a, b) => b.gross_amount - a.gross_amount).slice(0, 5));
        setRecentLabel("Today's");
      } else {
        // No sales today — show most recent from MTD
        const ms = mtdRes.data;
        if (ms.length > 0) {
          const lastDate = [...new Set(ms.map(s => s.date))].sort().pop();
          const lastDaySales = ms.filter(s => s.date === lastDate);
          setTodaySales(lastDaySales.sort((a, b) => b.gross_amount - a.gross_amount).slice(0, 5));
          setRecentLabel(`Recent (${fmtDate(lastDate)})`);
        }
      }
      const ms = mtdRes.data;
      const uniqueDates = [...new Set(ms.map(s => s.date))];
      setMtd({
        qty: ms.reduce((s, x) => s + x.quantity, 0),
        gross: ms.reduce((s, x) => s + x.gross_amount, 0),
        days: uniqueDates.length,
        todayQty: ts.reduce((s, x) => s + x.quantity, 0),
        todayGross: ts.reduce((s, x) => s + x.gross_amount, 0),
        todayEntries: ts.length,
        todayBeparis: [...new Set(ts.map(s => s.bepaari_id))].length,
      });
      const dukLedger = dukRes.data;
      setTopRecv(dukLedger.filter(d => d.balance > 0).sort((a, b) => b.balance - a.balance).slice(0, 5));
      
      // Overdue: balance > 0 and last_txn_date > 11 days ago
      const now = new Date();
      const overdueList = dukLedger
        .filter(d => {
          if (d.balance <= 0 || !d.last_txn_date) return false;
          const days = Math.floor((now - new Date(d.last_txn_date)) / (1000 * 60 * 60 * 24));
          return days > 11;
        })
        .map(d => ({ ...d, days_old: Math.floor((now - new Date(d.last_txn_date)) / (1000 * 60 * 60 * 24)) }))
        .sort((a, b) => b.balance - a.balance);
      setOverdue(overdueList);
      setTopPay(bepRes.data.filter(b => b.balance > 0).sort((a, b) => b.balance - a.balance).slice(0, 5));
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="loading">Loading...</div>;

  const comm = data?.liabilities?.commission || {};
  const patti = data?.assets?.patti || 0;
  const bepPay = data?.liabilities?.bepaari_payables || 0;
  const todayAvg = mtd?.todayQty > 0 ? Math.round(mtd.todayGross / mtd.todayQty) : 0;

  return (
    <div className="dalal-dashboard" data-testid="dashboard-page">
      {/* Header */}
      <div className="dalal-header">
        <div>
          <p className="dalal-firm-name">Haji Mushtaq Nana & Sons</p>
          <h1 className="dalal-h1">Dashboard</h1>
          <p className="dalal-date">{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
        {isAdmin && (
          <div className={`dalal-badge ${data?.difference === 0 ? 'tallied' : 'pending'}`} data-testid="bs-status-badge">
            {data?.difference === 0 ? 'BALANCE SHEET TALLIED' : `DIFF: ${formatCurrency(data?.difference)}`}
          </div>
        )}
      </div>

      {/* MTD Bar */}
      <div className="dalal-mtd-bar">
        <div className="dalal-mtd-label">
          <span className="dalal-mtd-title">MTD SUMMARY</span>
          <span className="dalal-mtd-month">{new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</span>
        </div>
        <div className="dalal-mtd-stats">
          <div className="dalal-mtd-stat">
            <span className="dalal-mtd-value">{mtd?.qty?.toLocaleString('en-IN')}</span>
            <span className="dalal-mtd-stat-label">Total Goats</span>
          </div>
          <div className="dalal-mtd-divider" />
          <div className="dalal-mtd-stat">
            <span className="dalal-mtd-value">{fmtShort(mtd?.gross)}</span>
            <span className="dalal-mtd-stat-label">Gross Sales</span>
          </div>
          <div className="dalal-mtd-divider" />
          <div className="dalal-mtd-stat">
            <span className="dalal-mtd-value">{mtd?.days}</span>
            <span className="dalal-mtd-stat-label">Market Days</span>
          </div>
          {isAdmin && <>
            <div className="dalal-mtd-divider" />
            <div className="dalal-mtd-stat">
              <span className="dalal-mtd-value">{fmtShort(comm.total)}</span>
              <span className="dalal-mtd-stat-label">Net Commission</span>
            </div>
          </>}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="dalal-metrics" data-testid="metric-cards" style={isAdmin ? {} : {gridTemplateColumns: 'repeat(2, 1fr)'}}>
        <div className="dalal-metric-card" data-testid="metric-today-goats">
          <span className="dalal-metric-label">TODAY'S GOATS</span>
          <span className="dalal-metric-value">{mtd?.todayQty || 0}</span>
          <span className="dalal-metric-sub">{mtd?.todayEntries || 0} txns | {mtd?.todayBeparis || 0} bepaaris</span>
        </div>
        <div className="dalal-metric-card" data-testid="metric-today-sales">
          <span className="dalal-metric-label">TODAY'S SALES</span>
          <span className="dalal-metric-value">{fmtShort(mtd?.todayGross)}</span>
          <span className="dalal-metric-sub">{todayAvg > 0 ? `Avg ${formatCurrency(todayAvg)}/head` : 'No sales today'}</span>
        </div>
        {isAdmin && <>
          <div className="dalal-metric-card" data-testid="commission-card">
            <span className="dalal-metric-label">NET COMMISSION</span>
            <span className="dalal-metric-value">{fmtShort(comm.total)}</span>
            <span className="dalal-metric-sub">Gross {fmtShort(comm.earned)}{comm.rate_diff > 0 ? ` +${fmtShort(comm.rate_diff)}` : ''} - Disc {fmtShort(comm.discounts)}</span>
          </div>
          <div className="dalal-metric-card" data-testid="metric-cash">
            <span className="dalal-metric-label">CASH BALANCE</span>
            <span className="dalal-metric-value cash">{formatCurrency(data?.assets?.cash_balance)}</span>
            <span className="dalal-metric-sub">&nbsp;</span>
          </div>
          <div className="dalal-metric-card" data-testid="metric-bank">
            <span className="dalal-metric-label">BANK BALANCE</span>
            <span className="dalal-metric-value">{formatCurrency(data?.assets?.bank_balance)}</span>
            <span className="dalal-metric-sub">&nbsp;</span>
          </div>
        </>}
      </div>

      {/* Three Columns */}
      <div className="dalal-three-col">
        {/* Today's / Recent Transactions */}
        <div className="dalal-table-card">
          <div className="dalal-table-header">
            <h3>{recentLabel} Transactions</h3>
            <span className="dalal-link" onClick={() => navigate('/daily-sales')} data-testid="view-all-sales">View All</span>
          </div>
          <table className="dalal-table">
            <thead><tr><th>BEPAARI &rarr; DUKANDAR</th><th style={{textAlign:'center'}}>QTY</th><th style={{textAlign:'right'}}>AMOUNT</th></tr></thead>
            <tbody>
              {todaySales.length === 0 ? (
                <tr><td colSpan="3" className="dalal-empty">No sales today</td></tr>
              ) : todaySales.map((s, i) => (
                <tr key={i} className={i % 2 === 1 ? 'alt-row' : ''}>
                  <td><strong>{s.bepaari_name}</strong><span className="dalal-arrow"> &rarr; </span><span className="dalal-muted">{s.dukandar_name}</span></td>
                  <td style={{textAlign:'center'}}>{s.quantity} pcs</td>
                  <td style={{textAlign:'right'}} className="dalal-amount">{formatCurrency(s.gross_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top Receivables */}
        <div className="dalal-table-card">
          <div className="dalal-table-header">
            <h3>Top Receivables (Patti)</h3>
            <span className="dalal-link" onClick={() => navigate('/dukandar-ledger')} data-testid="view-dukandar-ledger">Ledger</span>
          </div>
          <table className="dalal-table">
            <thead><tr><th>DUKANDAR</th><th style={{textAlign:'right'}}>BALANCE</th></tr></thead>
            <tbody>
              {topRecv.map((d, i) => (
                <tr key={i} className={i % 2 === 1 ? 'alt-row' : ''}>
                  <td><strong>{d.name}</strong></td>
                  <td style={{textAlign:'right'}} className="dalal-amount recv">{formatCurrency(d.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="dalal-total-bar">
            <span>Total Patti</span>
            <span className="dalal-total-value">{formatCurrency(patti)}</span>
          </div>
        </div>

        {/* Top Payables */}
        <div className="dalal-table-card">
          <div className="dalal-table-header">
            <h3>Top Payables (Bepaari)</h3>
            <span className="dalal-link" onClick={() => navigate('/bepaari-ledger')} data-testid="view-bepaari-ledger">Ledger</span>
          </div>
          <table className="dalal-table">
            <thead><tr><th>BEPAARI</th><th style={{textAlign:'right'}}>BALANCE</th></tr></thead>
            <tbody>
              {topPay.map((b, i) => (
                <tr key={i} className={i % 2 === 1 ? 'alt-row' : ''}>
                  <td><strong>{b.name}</strong></td>
                  <td style={{textAlign:'right'}} className="dalal-amount pay">{formatCurrency(b.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="dalal-total-bar">
            <span>Total Payable</span>
            <span className="dalal-total-value">{formatCurrency(bepPay)}</span>
          </div>
        </div>
      </div>

      {/* Overdue Section */}
      {overdue.length > 0 && (
        <div className="dalal-overdue-section" data-testid="overdue-section">
          <div className="dalal-table-card">
            <div className="dalal-table-header">
              <h3>Overdue Receivables ({overdue.length})</h3>
              <span className="dalal-link" onClick={() => navigate('/dukandar-ledger')} data-testid="view-overdue-ledger">Ledger</span>
            </div>
            <table className="dalal-table">
              <thead><tr><th>DUKANDAR</th><th style={{textAlign:'center'}}>DAYS</th><th style={{textAlign:'center'}}>LAST TXN</th><th style={{textAlign:'right'}}>BALANCE</th></tr></thead>
              <tbody>
                {overdue.map((d, i) => (
                  <tr key={i} className={d.days_old > 15 ? 'aging-red' : 'aging-yellow'}>
                    <td><strong>{d.name}</strong></td>
                    <td style={{textAlign:'center'}}><span className="aging-badge">{d.days_old}d</span></td>
                    <td style={{textAlign:'center', fontSize: 12, color: '#475569'}}>{fmtDate(d.last_txn_date)}</td>
                    <td style={{textAlign:'right'}} className="dalal-amount recv">{formatCurrency(d.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== DAILY SALES ==============
const DailySales = () => {
  const [sales, setSales] = useState([]);
  const [bepaaris, setBeparis] = useState([]);
  const [dukandars, setDukandars] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], bepaari_id: "", dukandar_id: "", quantity: "", rate: "", discount: "0", dukandar_rate: "", comment: "" });
  const [filters, setFilters] = useState({ fromDate: "", toDate: "", bepaari_id: "", dukandar_id: "" });
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      let url = `${API}/daily-sales?`;
      if (filters.fromDate) url += `from_date=${filters.fromDate}&`;
      if (filters.toDate) url += `to_date=${filters.toDate}&`;
      if (filters.bepaari_id) url += `bepaari_id=${filters.bepaari_id}&`;
      if (filters.dukandar_id) url += `dukandar_id=${filters.dukandar_id}&`;
      
      const [salesRes, bepaariRes, dukandarRes] = await Promise.all([
        axios.get(url), axios.get(`${API}/bepaaris`), axios.get(`${API}/dukandars`)
      ]);
      setSales(salesRes.data);
      setBeparis(bepaariRes.data);
      setDukandars(dukandarRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/daily-sales`, { ...form, quantity: parseInt(form.quantity), rate: parseFloat(form.rate), discount: parseFloat(form.discount || 0), dukandar_rate: form.dukandar_rate ? parseFloat(form.dukandar_rate) : null, comment: form.comment ? form.comment.trim() : null });
    setForm({ ...form, bepaari_id: "", dukandar_id: "", quantity: "", rate: "", discount: "0", dukandar_rate: "", comment: "" });
    fetchData();
  };

  const handleDelete = async (id) => { if (window.confirm("Delete?")) { await axios.delete(`${API}/daily-sales/${id}`); fetchData(); } };

  const handleEdit = (sale) => {
    setEditItem(sale);
    setEditForm({
      date: sale.date,
      bepaari_id: sale.bepaari_id,
      dukandar_id: sale.dukandar_id,
      quantity: sale.quantity,
      rate: sale.rate,
      discount: sale.discount || 0,
      dukandar_rate: sale.dukandar_rate || "",
      comment: sale.comment || ""
    });
  };

  const handleEditSave = async () => {
    await axios.put(`${API}/daily-sales/${editItem.id}`, {
      ...editForm,
      quantity: parseInt(editForm.quantity),
      rate: parseFloat(editForm.rate),
      discount: parseFloat(editForm.discount || 0),
      dukandar_rate: editForm.dukandar_rate ? parseFloat(editForm.dukandar_rate) : null,
      comment: editForm.comment ? editForm.comment.trim() : null
    });
    setEditItem(null);
    fetchData();
  };

  const clearFilters = () => setFilters({ fromDate: "", toDate: "", bepaari_id: "", dukandar_id: "" });

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page">
      <h2>Daily Sales</h2>
      
      <form className="entry-form" onSubmit={handleSubmit}>
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        <SearchableSelect
          options={bepaaris.map(b => ({ value: b.id, label: b.name })).sort((a, b) => a.label.localeCompare(b.label))}
          value={form.bepaari_id}
          onChange={(val) => setForm({ ...form, bepaari_id: val })}
          placeholder="Select Bepaari"
          testId="bepaari-select"
        />
        <SearchableSelect
          options={dukandars.map(d => ({ value: d.id, label: d.name })).sort((a, b) => a.label.localeCompare(b.label))}
          value={form.dukandar_id}
          onChange={(val) => setForm({ ...form, dukandar_id: val })}
          placeholder="Select Dukandar"
          testId="dukandar-select"
        />
        <input type="number" placeholder="Qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required min="1" />
        <input type="number" placeholder="Rate" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} required />
        <input type="number" placeholder="Discount" value={form.discount === "0" ? "" : form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} onBlur={(e) => { if (!e.target.value) setForm({ ...form, discount: "0" }); }} />
        <span className="form-calc" data-testid="form-total">{form.quantity && form.rate ? `Total: ${formatCurrency(parseInt(form.quantity || 0) * parseFloat(form.rate || 0) - parseFloat(form.discount || 0))}` : ''}</span>
        <input type="number" placeholder="Duk. Rate (optional)" value={form.dukandar_rate} onChange={(e) => setForm({ ...form, dukandar_rate: e.target.value })} data-testid="dukandar-rate-input" title="Only fill if Dukandar rate differs from Bepaari rate" />
        <input type="text" placeholder="Comment (optional - shows in Aakda)" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="comment-input" className="comment-input" title="Optional note for this sale - will appear under this line in the Bepaari Aakda slip" />
        <button type="submit" className="btn-primary">Add Sale</button>
      </form>

      <div className="filter-bar">
        <label>From:</label><input type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} />
        <label>To:</label><input type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} />
        <SearchableSelect
          options={[{ value: "", label: "All Bepaaris" }, ...bepaaris.map(b => ({ value: b.id, label: b.name })).sort((a, b) => a.label.localeCompare(b.label))]}
          value={filters.bepaari_id}
          onChange={(val) => setFilters({ ...filters, bepaari_id: val })}
          placeholder="All Bepaaris"
        />
        <SearchableSelect
          options={[{ value: "", label: "All Dukandars" }, ...dukandars.map(d => ({ value: d.id, label: d.name })).sort((a, b) => a.label.localeCompare(b.label))]}
          value={filters.dukandar_id}
          onChange={(val) => setFilters({ ...filters, dukandar_id: val })}
          placeholder="All Dukandars"
        />
        <button className="btn-clear" onClick={clearFilters}>Clear</button>
      </div>

      <div className="table-container desktop-only">
        <table>
          <thead><tr><th>Date</th><th>Bepaari</th><th>Dukandar</th><th>Qty</th><th>Rate</th><th>Duk. Rate</th><th>Gross</th><th>Disc</th><th>Net</th><th>Comment</th><th>Actions</th></tr></thead>
          <tbody>
            {sales.map((s) => (
              <tr key={s.id}>
                <td>{fmtDate(s.date)}</td><td>{s.bepaari_name}</td><td>{s.dukandar_name}</td><td>{s.quantity}</td>
                <td>{formatCurrency(s.rate)}</td>
                <td>{s.dukandar_rate ? formatCurrency(s.dukandar_rate) : "—"}</td>
                <td>{formatCurrency(s.gross_amount)}</td><td>{formatCurrency(s.discount)}</td><td>{formatCurrency(s.net_amount)}</td>
                <td className="comments-col" title={s.comment || ""}>{s.comment || "—"}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleEdit(s)}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(s.id)}>X</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="mobile-cards mobile-only">
        {sales.map((s) => (
          <div key={s.id} className="mobile-entry-card" data-testid="sale-card">
            <div className="mec-header">
              <span className="mec-date">{fmtDate(s.date)}</span>
              <span className="mec-net">{formatCurrency(s.net_amount)}</span>
            </div>
            <div className="mec-parties">
              <strong>{s.bepaari_name}</strong> <span className="dalal-arrow">&rarr;</span> {s.dukandar_name}
            </div>
            <div className="mec-details">
              <span>{s.quantity} pcs @ {formatCurrency(s.rate)}</span>
              <span>Gross: {formatCurrency(s.gross_amount)}</span>
              {s.discount > 0 && <span>Disc: -{formatCurrency(s.discount)}</span>}
              {s.dukandar_rate && <span>Duk Rate: {formatCurrency(s.dukandar_rate)}</span>}
            </div>
            {s.comment && (
              <div className="mec-comment">
                <span className="mec-comment-label">Note:</span> {s.comment}
              </div>
            )}
            <div className="mec-actions">
              <button className="btn-edit" onClick={() => handleEdit(s)}>Edit</button>
              <button className="btn-delete" onClick={() => handleDelete(s.id)}>X</button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editItem && (
        <div className="modal-overlay" onClick={() => setEditItem(null)}>
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <h3>Edit Sale</h3>
            <div className="edit-form">
              <label>Date:<input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} /></label>
              <label>Bepaari:
                <select value={editForm.bepaari_id} onChange={(e) => setEditForm({ ...editForm, bepaari_id: e.target.value })}>
                  {bepaaris.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
              <label>Dukandar:
                <select value={editForm.dukandar_id} onChange={(e) => setEditForm({ ...editForm, dukandar_id: e.target.value })}>
                  {dukandars.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
              <label>Qty:<input type="number" value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} /></label>
              <label>Rate:<input type="number" value={editForm.rate} onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })} /></label>
              <label>Discount:<input type="number" value={editForm.discount} onChange={(e) => setEditForm({ ...editForm, discount: e.target.value })} /></label>
              <label>Duk. Rate:<input type="number" placeholder="Optional" value={editForm.dukandar_rate} onChange={(e) => setEditForm({ ...editForm, dukandar_rate: e.target.value })} /></label>
              <label>Comment:<input type="text" placeholder="Optional - shows in Aakda" value={editForm.comment || ""} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} /></label>
            </div>
            <div className="modal-actions">
              <button className="btn-clear" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== CASH BOOK ==============
const CashBook = () => {
  const [entries, setEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]); // Store all entries for client-side filtering
  const [allUnfiltered, setAllUnfiltered] = useState([]); // ALL entries ignoring filter — for summary calc
  const [allAdjustments, setAllAdjustments] = useState([]);
  const [settings, setSettings] = useState({ opening_cash: 0, opening_bank: 0 });
  const [parties, setParties] = useState([]);
  const [balanceMap, setBalanceMap] = useState({});  // `${ptype}:${id}` → current balance
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], type: "", sub_type: "", party_id: "", amount: "", bf_disc: "", mode: "CASH", particulars: "" });
  const [filters, setFilters] = useState({ fromDate: "", toDate: "", type: "", subType: "", party: "", mode: "" });
  const [sortBy, setSortBy] = useState("date-desc");
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAllRows, setShowAllRows] = useState(false);  // limit rendered rows for performance
  const VISIBLE_ROW_LIMIT = 100;

  const types = ["BEPAARI", "DUKANDAR", "CAPITAL", "LOAN", "AMANAT", "ADVANCE", "EXPENSE", "ZAKAT"];
  const subTypes = {
    BEPAARI: ["PAYMENT", "DAILY_EXPENSES", "MOTOR", "BHUSSA", "GAWALI", "CASH_ADV"],
    DUKANDAR: ["RECEIPT", "REFUND"],
    CAPITAL: ["TAKEN", "REPAID", "WITHDRAWN"],
    LOAN: ["TAKEN", "REPAID"],
    AMANAT: ["TAKEN", "REPAID"],
    ADVANCE: ["GIVEN", "RECEIVED"],
    EXPENSE: ["MANDI", "TRAVEL", "FOOD", "SALARY", "MHN_PERSONAL", "JB_PAID", "MISC", "OTHER"],
    ZAKAT: ["PROVISION", "PAID"]
  };
  const modes = ["CASH", "BANK", "UPI", "TRANSFER"];

  // Composite expense fields (only used when sub_type === "DAILY_EXPENSES")
  const [dailyExp, setDailyExp] = useState({ motor: "", bhussa: "", gawali: "", cash_adv: "" });

  // Fast path (post-insert): only refetch cash-book entries + adjustments.
  const refreshEntries = async () => {
    try {
      let url = `${API}/cash-book?`;
      if (filters.fromDate) url += `from_date=${filters.fromDate}&`;
      if (filters.toDate) url += `to_date=${filters.toDate}&`;
      const [entriesRes, allRes, adjRes, bepLedgerRes, dukLedgerRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/cash-book`),
        axios.get(`${API}/adjustments`),
        axios.get(`${API}/bepaari-ledger`).catch(() => ({ data: [] })),
        axios.get(`${API}/dukandar-ledger`).catch(() => ({ data: [] })),
      ]);
      setAllEntries(entriesRes.data);
      setEntries(entriesRes.data);
      setAllUnfiltered(allRes.data);
      setAllAdjustments(adjRes.data || []);
      const balMap = {};
      (bepLedgerRes.data || []).forEach(b => { balMap[`BEPAARI:${b.id}`] = b.balance || 0; });
      (dukLedgerRes.data || []).forEach(d => { balMap[`DUKANDAR:${d.id}`] = d.balance || 0; });
      setBalanceMap(balMap);
    } catch (err) { console.error(err); }
  };

  const fetchData = async () => {
    try {
      let url = `${API}/cash-book?`;
      if (filters.fromDate) url += `from_date=${filters.fromDate}&`;
      if (filters.toDate) url += `to_date=${filters.toDate}&`;

      const [entriesRes, bepaarisRes, dukandarsRes, advRes, capRes, allRes, settingsRes, adjRes, bepLedgerRes, dukLedgerRes] = await Promise.all([
        axios.get(url), axios.get(`${API}/bepaaris`), axios.get(`${API}/dukandars`),
        axios.get(`${API}/advance-parties`), axios.get(`${API}/capital-partners`),
        axios.get(`${API}/cash-book`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/adjustments`),
        axios.get(`${API}/bepaari-ledger`).catch(() => ({ data: [] })),
        axios.get(`${API}/dukandar-ledger`).catch(() => ({ data: [] })),
      ]);
      setAllEntries(entriesRes.data);
      setEntries(entriesRes.data);
      setAllUnfiltered(allRes.data);
      setSettings(settingsRes.data || { opening_cash: 0, opening_bank: 0 });
      setAllAdjustments(adjRes.data || []);
      const balMap = {};
      (bepLedgerRes.data || []).forEach(b => { balMap[`BEPAARI:${b.id}`] = b.balance || 0; });
      (dukLedgerRes.data || []).forEach(d => { balMap[`DUKANDAR:${d.id}`] = d.balance || 0; });
      setBalanceMap(balMap);
      setParties([
        ...bepaarisRes.data.map(p => ({ ...p, ptype: "BEPAARI" })),
        ...dukandarsRes.data.map(p => ({ ...p, ptype: "DUKANDAR" })),
        ...advRes.data.map(p => ({ ...p, ptype: "ADVANCE" })),
        ...capRes.data.map(p => ({ ...p, ptype: p.partner_type }))
      ]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const applyFilters = () => { fetchData(); };

  const filteredParties = parties.filter(p => {
    if (form.type === "BEPAARI") return p.ptype === "BEPAARI";
    if (form.type === "DUKANDAR") return p.ptype === "DUKANDAR";
    if (form.type === "ADVANCE") return p.ptype === "ADVANCE";
    if (["CAPITAL", "LOAN", "AMANAT"].includes(form.type)) return p.ptype === form.type;
    return false;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Special composite: DAILY_EXPENSES splits into 3 separate cash_book entries (Motor/Bhussa/Gawali)
    if (form.type === "BEPAARI" && form.sub_type === "DAILY_EXPENSES") {
      const items = [
        { sub_type: "MOTOR", amount: parseFloat(dailyExp.motor || 0) },
        { sub_type: "BHUSSA", amount: parseFloat(dailyExp.bhussa || 0) },
        { sub_type: "GAWALI", amount: parseFloat(dailyExp.gawali || 0) },
        { sub_type: "CASH_ADV", amount: parseFloat(dailyExp.cash_adv || 0) },
      ].filter(i => i.amount > 0);
      if (items.length === 0) {
        alert("Enter at least one of Motor / Bhussa / Gawali / Cash Adv");
        return;
      }
      // Create entries sequentially so a failure on one doesn't leave gaps unaccounted
      for (const it of items) {
        await axios.post(`${API}/cash-book`, {
          date: form.date,
          type: "BEPAARI",
          sub_type: it.sub_type,
          party_id: form.party_id,
          amount: it.amount,
          bf_disc: 0,
          mode: form.mode,
          particulars: form.particulars || "",
        });
      }
      // Optimistic balance update: each of these expense subtypes REDUCES our payable (we paid the bepaari)
      // So bepaari balance goes DOWN by the total.
      const totalDeduction = items.reduce((s, i) => s + i.amount, 0);
      const key = `BEPAARI:${form.party_id}`;
      setBalanceMap(prev => ({ ...prev, [key]: (prev[key] || 0) - totalDeduction }));
      setDailyExp({ motor: "", bhussa: "", gawali: "", cash_adv: "" });
      setForm({ ...form, type: "", sub_type: "", party_id: "", amount: "", bf_disc: "", particulars: "" });
      refreshEntries();
      return;
    }
    const amt = parseFloat(form.amount);
    await axios.post(`${API}/cash-book`, { 
      ...form, 
      amount: amt,
      bf_disc: form.bf_disc ? parseFloat(form.bf_disc) : 0
    });
    // Optimistic balance hint update for the inline "Currently payable/receivable" widget
    if (form.party_id) {
      const key = `${form.type}:${form.party_id}`;
      const bfDisc = form.bf_disc ? parseFloat(form.bf_disc) : 0;
      let delta = 0;
      // For Bepaari (payable, +ve = we owe): payment/expense REDUCES payable → delta = -amt
      // For Dukandar (receivable, +ve = they owe): receipt REDUCES receivable → delta = -(amt - bf_disc); refund INCREASES → +amt
      if (form.type === "BEPAARI") {
        if (form.sub_type === "PAYMENT") delta = -amt;
        else if (["MOTOR","BHUSSA","GAWALI","CASH_ADV"].includes(form.sub_type)) delta = -amt;
      } else if (form.type === "DUKANDAR") {
        if (form.sub_type === "RECEIPT") delta = -(amt - bfDisc);
        else if (form.sub_type === "REFUND") delta = +amt;
      }
      if (delta !== 0) setBalanceMap(prev => ({ ...prev, [key]: (prev[key] || 0) + delta }));
    }
    setForm({ ...form, type: "", sub_type: "", party_id: "", amount: "", bf_disc: "", particulars: "" });
    refreshEntries();
  };

  const handleDelete = async (id) => { if (window.confirm("Delete?")) { await axios.delete(`${API}/cash-book/${id}`); refreshEntries(); } };

  const handleEdit = (entry) => {
    setEditItem(entry);
    setEditForm({
      date: entry.date,
      type: entry.type,
      sub_type: entry.sub_type,
      party_id: entry.party_id || "",
      amount: entry.amount,
      bf_disc: entry.bf_disc || 0,
      mode: entry.mode,
      particulars: entry.particulars || ""
    });
  };

  const handleEditSave = async () => {
    await axios.put(`${API}/cash-book/${editItem.id}`, {
      ...editForm,
      amount: parseFloat(editForm.amount),
      bf_disc: editForm.bf_disc ? parseFloat(editForm.bf_disc) : 0
    });
    setEditItem(null);
    refreshEntries();
  };

  // Client-side filtering based on column filters
  const filteredEntries = useMemo(() => allEntries.filter(e => {
    if (filters.type && e.type !== filters.type) return false;
    if (filters.subType && e.sub_type !== filters.subType) return false;
    if (filters.party && e.party_name !== filters.party) return false;
    if (filters.mode && e.mode !== filters.mode) return false;
    return true;
  }), [allEntries, filters]);

  // Sorting
  const sortedEntries = useMemo(() => [...filteredEntries].sort((a, b) => {
    if (sortBy === "date-desc") return b.date.localeCompare(a.date);
    if (sortBy === "date-asc") return a.date.localeCompare(b.date);
    if (sortBy === "type-asc") return (a.type || "").localeCompare(b.type || "");
    if (sortBy === "type-desc") return (b.type || "").localeCompare(a.type || "");
    if (sortBy === "subtype-asc") return (a.sub_type || "").localeCompare(b.sub_type || "");
    if (sortBy === "subtype-desc") return (b.sub_type || "").localeCompare(a.sub_type || "");
    if (sortBy === "party-asc") return (a.party_name || "").localeCompare(b.party_name || "");
    if (sortBy === "party-desc") return (b.party_name || "").localeCompare(a.party_name || "");
    if (sortBy === "amount-desc") return b.amount - a.amount;
    if (sortBy === "amount-asc") return a.amount - b.amount;
    if (sortBy === "mode-asc") return (a.mode || "").localeCompare(b.mode || "");
    if (sortBy === "mode-desc") return (b.mode || "").localeCompare(a.mode || "");
    return 0;
  }), [filteredEntries, sortBy]);

  // Get unique values for dropdown filters (memoized)
  const uniqueParties = useMemo(() => [...new Set(allEntries.map(e => e.party_name).filter(Boolean))].sort(), [allEntries]);
  const uniqueSubTypes = useMemo(() => [...new Set(allEntries.map(e => e.sub_type).filter(Boolean))].sort(), [allEntries]);

  // Calculate filtered total (memoized)
  const filteredTotal = useMemo(() => sortedEntries.reduce((sum, e) => sum + (e.amount || 0), 0), [sortedEntries]);

  // PERFORMANCE FIX: only render top-N rows by default. Months of entries cause hangs
  // on every keystroke because React re-renders all rows. Cap at 100 unless user toggles.
  const visibleEntries = useMemo(
    () => (showAllRows ? sortedEntries : sortedEntries.slice(0, VISIBLE_ROW_LIMIT)),
    [sortedEntries, showAllRows]
  );

  // Show BF_Disc field only for DUKANDAR RECEIPT
  const showBfDisc = form.type === "DUKANDAR" && form.sub_type === "RECEIPT";
  const showDailyExp = form.type === "BEPAARI" && form.sub_type === "DAILY_EXPENSES";

  // ===== Live Cash & Bank summary (mirrors backend balance-sheet logic) =====
  const [summaryDate, setSummaryDate] = useState(new Date().toISOString().split('T')[0]);
  const [summaryMode, setSummaryMode] = useState("daily"); // 'daily' | 'all-time'

  // useMemo so this expensive aggregation only runs when underlying data/date changes,
  // NOT on every keystroke or form-state change.
  const summary = useMemo(() => {
    const CASH_IN = ["RECEIPT", "TAKEN", "RECEIVED"];
    const BANK_MODES = ["BANK", "UPI", "TRANSFER"];
    const calcInOut = (es, modeFilter) => {
      let inn = 0, out = 0;
      es.forEach(c => {
        const mode = c.mode || "CASH";
        const isThisMode = modeFilter === "CASH" ? mode === "CASH" : BANK_MODES.includes(mode);
        if (!isThisMode) return;
        const inflow = CASH_IN.includes(c.sub_type);
        const realAmt = (c.sub_type === "RECEIPT") ? (c.amount - (c.bf_disc || 0)) : c.amount;
        if (inflow) inn += realAmt; else out += c.amount;
      });
      return { inn, out };
    };

    let before = [];
    let within = [];
    let adjBefore = [];
    let adjWithin = [];
    if (summaryMode === "daily") {
      before = allUnfiltered.filter(e => e.date < summaryDate);
      within = allUnfiltered.filter(e => e.date === summaryDate);
      adjBefore = allAdjustments.filter(a => a.date < summaryDate);
      adjWithin = allAdjustments.filter(a => a.date === summaryDate);
    } else {
      within = allUnfiltered;
      adjWithin = allAdjustments;
    }

    // Helper: net JV impact on a specific head (CASH or BANK)
    // Debit X = X is the receiver (money coming IN)
    // Credit X = X is the payer (money going OUT)
    const calcAdjFlow = (adjs, head) => {
      let inn = 0, out = 0;
      adjs.forEach(a => {
        if (a.debit_type === head) inn += a.amount;
        if (a.credit_type === head) out += a.amount;
      });
      return { inn, out };
    };

    // Cash
    const cashBefore = calcInOut(before, "CASH");
    const cashWithin = calcInOut(within, "CASH");
    const cashAdjBefore = calcAdjFlow(adjBefore, "CASH");
    const cashAdjWithin = calcAdjFlow(adjWithin, "CASH");
    const cashOpening = (settings.opening_cash || 0) + cashBefore.inn - cashBefore.out + cashAdjBefore.inn - cashAdjBefore.out;
    const cashClosing = cashOpening + cashWithin.inn + cashAdjWithin.inn - cashWithin.out - cashAdjWithin.out;

    // Bank
    const bankBefore = calcInOut(before, "BANK");
    const bankWithin = calcInOut(within, "BANK");
    const bankAdjBefore = calcAdjFlow(adjBefore, "BANK");
    const bankAdjWithin = calcAdjFlow(adjWithin, "BANK");
    const bankOpening = (settings.opening_bank || 0) + bankBefore.inn - bankBefore.out + bankAdjBefore.inn - bankAdjBefore.out;
    const bankClosing = bankOpening + bankWithin.inn + bankAdjWithin.inn - bankWithin.out - bankAdjWithin.out;

    return {
      cash: { opening: cashOpening, in: cashWithin.inn + cashAdjWithin.inn, out: cashWithin.out + cashAdjWithin.out, closing: cashClosing },
      bank: { opening: bankOpening, in: bankWithin.inn + bankAdjWithin.inn, out: bankWithin.out + bankAdjWithin.out, closing: bankClosing }
    };
  }, [allUnfiltered, allAdjustments, settings, summaryDate, summaryMode]);

  if (loading) return <div className="loading">Loading...</div>;

  const cardLabel = summaryMode === "all-time" ? "ALL-TIME" : `As on ${fmtDate(summaryDate)}`;

  return (
    <div className="page">
      <h2>Cash & Bank</h2>

      {/* Live Summary Strip with date picker */}
      <div className="cb-summary-controls">
        <div className="cb-summary-tabs">
          <button
            className={`cbst-btn ${summaryMode === 'daily' ? 'active' : ''}`}
            onClick={() => setSummaryMode('daily')}
            data-testid="cbst-daily">Daily</button>
          <button
            className={`cbst-btn ${summaryMode === 'all-time' ? 'active' : ''}`}
            onClick={() => setSummaryMode('all-time')}
            data-testid="cbst-alltime">All-Time</button>
        </div>
        {summaryMode === 'daily' && (
          <input
            type="date"
            value={summaryDate}
            onChange={(e) => setSummaryDate(e.target.value)}
            className="cb-summary-date"
            data-testid="cb-summary-date"
          />
        )}
      </div>

      <div className="cb-summary-strip" data-testid="cb-summary">
        <div className="cb-summary-card cb-cash">
          <div className="cbsc-header">
            <span className="cbsc-icon">💵</span>
            <span className="cbsc-title">CASH</span>
            <span className="cbsc-period">{cardLabel}</span>
          </div>
          <div className="cbsc-row"><span>Opening</span><span className="cbsc-mono">{formatCurrency(summary.cash.opening)}</span></div>
          <div className="cbsc-row cbsc-in"><span>+ Cash In</span><span className="cbsc-mono">{formatCurrency(summary.cash.in)}</span></div>
          <div className="cbsc-row cbsc-out"><span>− Cash Out</span><span className="cbsc-mono">{formatCurrency(summary.cash.out)}</span></div>
          <div className="cbsc-row cbsc-closing"><span>Closing</span><span className="cbsc-mono">{formatCurrency(summary.cash.closing)}</span></div>
        </div>
        <div className="cb-summary-card cb-bank">
          <div className="cbsc-header">
            <span className="cbsc-icon">🏦</span>
            <span className="cbsc-title">BANK</span>
            <span className="cbsc-period">{cardLabel}</span>
          </div>
          <div className="cbsc-row"><span>Opening</span><span className="cbsc-mono">{formatCurrency(summary.bank.opening)}</span></div>
          <div className="cbsc-row cbsc-in"><span>+ Bank In</span><span className="cbsc-mono">{formatCurrency(summary.bank.in)}</span></div>
          <div className="cbsc-row cbsc-out"><span>− Bank Out</span><span className="cbsc-mono">{formatCurrency(summary.bank.out)}</span></div>
          <div className="cbsc-row cbsc-closing"><span>Closing</span><span className="cbsc-mono">{formatCurrency(summary.bank.closing)}</span></div>
        </div>
      </div>
      
      <form className="entry-form" onSubmit={handleSubmit}>
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, sub_type: "", party_id: "", bf_disc: "" })} required>
          <option value="">Type</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={form.sub_type} onChange={(e) => setForm({ ...form, sub_type: e.target.value, bf_disc: "" })} required disabled={!form.type}>
          <option value="">Sub-Type</option>
          {(subTypes[form.type] || []).map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
        <SearchableSelect
          options={filteredParties.map(p => ({ value: p.id, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
          value={form.party_id}
          onChange={(val) => setForm({ ...form, party_id: val })}
          placeholder="Party"
          disabled={!form.type}
          testId="cb-party-select"
        />
        {/* Inline current balance hint */}
        {form.party_id && ["BEPAARI","DUKANDAR"].includes(form.type) && (() => {
          const bal = balanceMap[`${form.type}:${form.party_id}`];
          if (bal == null) return null;
          const isPayment = form.type === "BEPAARI" && form.sub_type === "PAYMENT";
          const isReceipt = form.type === "DUKANDAR" && form.sub_type === "RECEIPT";
          const showFillBtn = (isPayment && bal > 0) || (isReceipt && bal > 0);
          const label = form.type === "BEPAARI"
            ? (bal > 0 ? `Currently payable: ${formatCurrency(bal)}` : bal < 0 ? `Bepaari owes us: ${formatCurrency(-bal)}` : `Settled — ₹0`)
            : (bal > 0 ? `Currently receivable: ${formatCurrency(bal)}` : bal < 0 ? `Advance with Dukandar: ${formatCurrency(-bal)}` : `Settled — ₹0`);
          const color = bal > 0 ? (form.type === "BEPAARI" ? "#dc2626" : "#16a34a") : bal < 0 ? "#94a3b8" : "#94a3b8";
          return (
            <div data-testid="cb-party-balance-hint" style={{
              width: '100%',
              fontSize: '12.5px',
              fontWeight: 600,
              color,
              padding: '4px 0 8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}>
              <span>↳ {label}</span>
              {showFillBtn && (
                <button
                  type="button"
                  data-testid="cb-fill-full-btn"
                  onClick={() => setForm({ ...form, amount: String(Math.abs(bal)) })}
                  style={{
                    background: '#1f2937',
                    color: '#fbbf24',
                    border: 'none',
                    padding: '3px 10px',
                    borderRadius: '5px',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >{isPayment ? '↳ Pay Full' : '↳ Receive Full'}</button>
              )}
            </div>
          );
        })()}
        <input type="number" placeholder="Amount (Full Payable)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required={!showDailyExp} disabled={showDailyExp} style={showDailyExp ? {display: 'none'} : {}} />
        {showDailyExp && (
          <>
            <input type="number" placeholder="Motor" value={dailyExp.motor} onChange={(e) => setDailyExp({ ...dailyExp, motor: e.target.value })} style={{maxWidth: '100px'}} data-testid="cb-motor-input" />
            <input type="number" placeholder="Bhussa" value={dailyExp.bhussa} onChange={(e) => setDailyExp({ ...dailyExp, bhussa: e.target.value })} style={{maxWidth: '100px'}} data-testid="cb-bhussa-input" />
            <input type="number" placeholder="Gawali" value={dailyExp.gawali} onChange={(e) => setDailyExp({ ...dailyExp, gawali: e.target.value })} style={{maxWidth: '100px'}} data-testid="cb-gawali-input" />
            <input type="number" placeholder="Cash Adv" value={dailyExp.cash_adv} onChange={(e) => setDailyExp({ ...dailyExp, cash_adv: e.target.value })} style={{maxWidth: '100px'}} data-testid="cb-cashadv-input" />
          </>
        )}
        {showBfDisc && (
          <input 
            type="number" 
            placeholder="BF Disc (if any)" 
            value={form.bf_disc} 
            onChange={(e) => setForm({ ...form, bf_disc: e.target.value })} 
            className="bf-disc-input"
            style={{maxWidth: '120px'}}
          />
        )}
        <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} required>
          {["CASH", "BANK", "UPI", "TRANSFER"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input type="text" placeholder="Comments (UPI ref, etc.)" value={form.particulars || ""} onChange={(e) => setForm({ ...form, particulars: e.target.value })} style={{minWidth: '180px'}} />
        <button type="submit" className="btn-primary">Add</button>
      </form>
      
      {showBfDisc && form.amount && (
        <div className="cash-calc-hint">
          Actual Cash Received: <strong>{formatCurrency(parseFloat(form.amount || 0) - parseFloat(form.bf_disc || 0))}</strong>
        </div>
      )}

      <div className="filter-bar">
        <label>From:</label><input type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} />
        <label>To:</label><input type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} />
        <button className="btn-primary" onClick={applyFilters}>Search</button>
        <button className="btn-clear" onClick={() => { setFilters({ fromDate: "", toDate: "", type: "", subType: "", party: "", mode: "" }); fetchData(); }}>Clear All</button>
        <button className="btn-print" onClick={() => {
          const printDate = new Date().toLocaleDateString('en-IN');
          const dateRange = filters.fromDate ? `${fmtDate(filters.fromDate)} to ${fmtDate(filters.toDate || 'Present')}` : 'All dates';
          const w = window.open('', '', 'width=1000,height=700');
          w.document.write(`<html><head><title>Cash & Bank</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
              body { font-family: 'IBM Plex Sans', sans-serif; padding: 20px; }
              .header { text-align: center; border-bottom: 3px solid #C5A55A; padding-bottom: 12px; margin-bottom: 16px; }
              .header h1 { font-family: 'Cormorant Garamond', serif; color: #1B2A4A; font-size: 22px; margin: 0; }
              .header .firm { color: #C5A55A; font-size: 11px; font-weight: 700; letter-spacing: 2px; margin: 4px 0; }
              .header .meta { color: #475569; font-size: 11px; }
              table { width: 100%; border-collapse: collapse; }
              th { background: #1B2A4A; color: white; padding: 8px 6px; font-size: 10px; text-transform: uppercase; text-align: left; }
              td { padding: 6px; border-bottom: 1px solid #E2E0D8; font-size: 11px; }
              .total { border-top: 3px solid #C5A55A; font-weight: 700; font-size: 13px; }
              .footer { text-align: center; margin-top: 16px; font-size: 9px; color: #94a3b8; border-top: 1px dashed #C5A55A; padding-top: 8px; }
            </style></head><body>
            <div class="header"><p class="firm">HAJI MUSHTAQ NANA & SONS</p><h1>CASH & BANK REGISTER</h1><p class="meta">${dateRange} | ${sortedEntries.length} entries | Generated: ${printDate}</p></div>
            <table><thead><tr><th>Date</th><th>Type</th><th>Sub-Type</th><th>Party</th><th>Amount</th><th>BF Disc</th><th>Mode</th><th>Comments</th></tr></thead><tbody>
            ${sortedEntries.map(e => `<tr><td>${fmtDate(e.date)}</td><td>${e.type}</td><td>${e.sub_type}</td><td>${e.party_name || '-'}</td><td style="text-align:right">${e.amount.toLocaleString('en-IN')}</td><td style="text-align:right">${e.bf_disc > 0 ? e.bf_disc.toLocaleString('en-IN') : '-'}</td><td>${e.mode}</td><td>${e.particulars || '-'}</td></tr>`).join('')}
            <tr class="total"><td colspan="4">Total (${sortedEntries.length} entries)</td><td style="text-align:right">${filteredTotal.toLocaleString('en-IN')}</td><td colspan="3"></td></tr>
            </tbody></table>
            <p class="footer">Generated from Mandi Accounting App | Haji Mushtaq Nana & Sons</p></body></html>`);
          w.document.close(); w.print();
        }}>Print / Save PDF</button>
        <button className="btn-secondary" data-testid="cb-excel-btn" onClick={() => {
          // CSV download (opens cleanly in Excel) — respects current filters and sort
          const dateRange = filters.fromDate && filters.toDate ? `${filters.fromDate}_to_${filters.toDate}` : filters.fromDate ? `from_${filters.fromDate}` : 'all';
          const headers = ["Date","Type","Sub-Type","Party","Amount","BF Disc","Mode","Comments"];
          const escapeCsv = (v) => {
            if (v === null || v === undefined) return "";
            const s = String(v);
            if (s.includes('"') || s.includes(',') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"';
            return s;
          };
          const rows = sortedEntries.map(e => [
            e.date,
            e.type,
            e.sub_type,
            e.party_name || '',
            e.amount,
            e.bf_disc || 0,
            e.mode,
            e.particulars || ''
          ].map(escapeCsv).join(','));
          // Filter context line for traceability
          const filterLine = `# Filters: ${[
            filters.fromDate && `from=${filters.fromDate}`,
            filters.toDate && `to=${filters.toDate}`,
            filters.type && `type=${filters.type}`,
            filters.subType && `subtype=${filters.subType}`,
            filters.party && `party=${filters.party}`,
            filters.mode && `mode=${filters.mode}`,
          ].filter(Boolean).join(' | ') || 'none'}`;
          const totalLine = `,,,Total (${sortedEntries.length}),${filteredTotal},,,`;
          const csv = "\ufeff" + filterLine + "\n" + headers.join(',') + "\n" + rows.join("\n") + "\n" + totalLine + "\n";
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `cash_bank_${dateRange}_${new Date().toISOString().slice(0,10)}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}>📥 Download Excel</button>
      </div>

      {/* Mobile Filters - dropdown row (only visible on mobile) */}
      <div className="mobile-filters mobile-only" data-testid="mobile-cash-filters">
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="m-filter-type">
          <option value="">Type ▼</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.subType} onChange={(e) => setFilters({ ...filters, subType: e.target.value })} data-testid="m-filter-subtype">
          <option value="">Sub-Type ▼</option>
          {uniqueSubTypes.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
        <select value={filters.party} onChange={(e) => setFilters({ ...filters, party: e.target.value })} data-testid="m-filter-party">
          <option value="">Party ▼</option>
          {uniqueParties.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.mode} onChange={(e) => setFilters({ ...filters, mode: e.target.value })} data-testid="m-filter-mode">
          <option value="">Mode ▼</option>
          {modes.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Always-visible row-count + Show All toggle (separate from filter tags) */}
      {sortedEntries.length > 0 && (
        <div style={{padding:'8px 12px', fontSize:'13px', color:'#475569', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap'}}>
          <span>
            Showing <strong>{showAllRows || sortedEntries.length <= VISIBLE_ROW_LIMIT ? sortedEntries.length : `${VISIBLE_ROW_LIMIT} of ${sortedEntries.length}`}</strong> entries · Total: <strong>{formatCurrency(filteredTotal)}</strong>
          </span>
          {sortedEntries.length > VISIBLE_ROW_LIMIT && (
            <button
              data-testid="cb-show-all-toggle"
              onClick={() => setShowAllRows(s => !s)}
              style={{background:'#1f2937', color:'#fbbf24', border:'none', padding:'4px 12px', borderRadius:'6px', fontSize:'12px', fontWeight:600, cursor:'pointer'}}
            >{showAllRows ? `↑ Show only latest ${VISIBLE_ROW_LIMIT}` : `↓ Show all ${sortedEntries.length}`}</button>
          )}
        </div>
      )}

      {(filters.type || filters.subType || filters.party || filters.mode) && (
        <div className="filter-summary">
          <strong>Filters:</strong> 
          {filters.type && <span className="filter-tag">{filters.type} <button onClick={() => setFilters({...filters, type: ""})}>×</button></span>}
          {filters.subType && <span className="filter-tag">{filters.subType} <button onClick={() => setFilters({...filters, subType: ""})}>×</button></span>}
          {filters.party && <span className="filter-tag">{filters.party} <button onClick={() => setFilters({...filters, party: ""})}>×</button></span>}
          {filters.mode && <span className="filter-tag">{filters.mode} <button onClick={() => setFilters({...filters, mode: ""})}>×</button></span>}
        </div>
      )}

      <div className="table-container desktop-only">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>
                <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="header-filter">
                  <option value="">Type ▼</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </th>
              <th>
                <select value={filters.subType} onChange={(e) => setFilters({ ...filters, subType: e.target.value })} className="header-filter">
                  <option value="">Sub-Type ▼</option>
                  {uniqueSubTypes.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </th>
              <th>
                <select value={filters.party} onChange={(e) => setFilters({ ...filters, party: e.target.value })} className="header-filter">
                  <option value="">Party ▼</option>
                  {uniqueParties.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </th>
              <th>Amount</th>
              <th>BF Disc</th>
              <th>
                <select value={filters.mode} onChange={(e) => setFilters({ ...filters, mode: e.target.value })} className="header-filter">
                  <option value="">Mode ▼</option>
                  {modes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </th>
              <th>Comments</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleEntries.map((e) => (
              <tr key={e.id}>
                <td>{fmtDate(e.date)}</td><td>{e.type}</td><td>{e.sub_type}</td><td>{e.party_name || "-"}</td>
                <td>{formatCurrency(e.amount)}</td>
                <td className="bf-disc-col">{e.bf_disc > 0 ? formatCurrency(e.bf_disc) : "-"}</td>
                <td>{e.mode}</td><td className="comments-col">{e.particulars || "-"}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleEdit(e)}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(e.id)}>X</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="mobile-cards mobile-only">
        {visibleEntries.map((e) => (
          <div key={e.id} className="mobile-entry-card" data-testid="cash-card">
            <div className="mec-header">
              <span className="mec-date">{fmtDate(e.date)}</span>
              <span className="mec-net">{formatCurrency(e.amount)}</span>
            </div>
            <div className="mec-parties">
              <span className="mec-badge">{e.type}</span>
              <span className="mec-badge sub">{e.sub_type}</span>
              <span className="mec-badge mode">{e.mode}</span>
            </div>
            {e.party_name && <div className="mec-party-name">{e.party_name}</div>}
            <div className="mec-details">
              {e.bf_disc > 0 && <span>BF Disc: {formatCurrency(e.bf_disc)}</span>}
              {e.particulars && <span>{e.particulars}</span>}
            </div>
            <div className="mec-actions">
              <button className="btn-edit" onClick={() => handleEdit(e)}>Edit</button>
              <button className="btn-delete" onClick={() => handleDelete(e.id)}>X</button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editItem && (
        <div className="modal-overlay" onClick={() => setEditItem(null)}>
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <h3>Edit Cash & Bank Entry</h3>
            <div className="edit-form">
              <label>Date:<input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} /></label>
              <label>Amount:<input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} /></label>
              {editForm.type === "DUKANDAR" && editForm.sub_type === "RECEIPT" && (
                <label>BF Disc:<input type="number" value={editForm.bf_disc} onChange={(e) => setEditForm({ ...editForm, bf_disc: e.target.value })} /></label>
              )}
              <label>Mode:
                <select value={editForm.mode} onChange={(e) => setEditForm({ ...editForm, mode: e.target.value })}>
                  {["CASH", "BANK", "UPI", "TRANSFER"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label>Comments:<input type="text" value={editForm.particulars} onChange={(e) => setEditForm({ ...editForm, particulars: e.target.value })} /></label>
            </div>
            <div className="modal-actions">
              <button className="btn-clear" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== ADJUSTMENTS / JOURNAL VOUCHER ==============
const Adjustments = () => {
  const [adjustments, setAdjustments] = useState([]);
  const [bepaaris, setBeparis] = useState([]);
  const [dukandars, setDukandars] = useState([]);
  const [advanceParties, setAdvanceParties] = useState([]);
  const [capitalPartners, setCapitalPartners] = useState([]);
  const [balanceMap, setBalanceMap] = useState({});
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    debit_type: "",
    debit_party_id: "",
    credit_type: "",
    credit_party_id: "",
    amount: "",
    narration: ""
  });
  // Quick Mode (purpose-driven adjustment) state
  const [quickMode, setQuickMode] = useState("MOVE"); // MOVE | WRITEOFF | CASH_DIFF | PROVISION | ADVANCED
  const [quick, setQuick] = useState({
    reduce_type: "",       // party type to reduce
    reduce_party_id: "",
    increase_type: "",     // party type / head to increase
    increase_party_id: "",
    offset_head: "MANDI_EXPENSE", // for WRITEOFF & CASH_DIFF & PROVISION
    cash_direction: "INCREASE",   // for CASH_DIFF: INCREASE = cash in hand > app; DECREASE = cash in hand < app
    cash_mode: "CASH",            // for CASH_DIFF: CASH or BANK
  });
  const [sortBy, setSortBy] = useState("date-desc");
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [adjRes, bepaariRes, dukandarRes, advRes, capRes, bepLedRes, dukLedRes] = await Promise.all([
        axios.get(`${API}/adjustments`),
        axios.get(`${API}/bepaaris`),
        axios.get(`${API}/dukandars`),
        axios.get(`${API}/advance-parties`),
        axios.get(`${API}/capital-partners`),
        axios.get(`${API}/bepaari-ledger`).catch(() => ({ data: [] })),
        axios.get(`${API}/dukandar-ledger`).catch(() => ({ data: [] }))
      ]);
      setAdjustments(adjRes.data);
      setBeparis(bepaariRes.data);
      setDukandars(dukandarRes.data);
      setAdvanceParties(advRes.data);
      setCapitalPartners(capRes.data);
      const bm = {};
      (bepLedRes.data || []).forEach(b => { bm[`BEPAARI:${b.id}`] = b.balance || 0; });
      (dukLedRes.data || []).forEach(d => { bm[`DUKANDAR:${d.id}`] = d.balance || 0; });
      // For advance/capital/loan/amanat — fall back to opening_balance (no daily-activity endpoint)
      advRes.data.forEach(a => { bm[`ADVANCE:${a.id}`] = a.opening_balance || 0; });
      capRes.data.forEach(c => { bm[`${c.partner_type}:${c.id}`] = c.opening_balance || 0; });
      setBalanceMap(bm);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getParties = (type) => {
    if (type === "BEPAARI") return bepaaris;
    if (type === "DUKANDAR") return dukandars;
    if (type === "ADVANCE") return advanceParties;
    if (type === "CAPITAL") return capitalPartners.filter(p => p.partner_type === "CAPITAL");
    if (type === "LOAN") return capitalPartners.filter(p => p.partner_type === "LOAN");
    if (type === "AMANAT") return capitalPartners.filter(p => p.partner_type === "AMANAT");
    if (type === "MANDI_EXPENSE" || type === "BF_DISCOUNT" || type === "MHN_PERSONAL"
        || type === "COMMISSION" || type === "KK" || type === "JB" || type === "ZAKAT"
        || type === "CASH" || type === "BANK") return []; // No party needed
    return [];
  };

  const HEAD_TYPES = ["MANDI_EXPENSE","BF_DISCOUNT","MHN_PERSONAL","COMMISSION","KK","JB","ZAKAT","CASH","BANK"];
  const isExpenseHead = (type) => HEAD_TYPES.includes(type);

  // ----- Quick Mode helpers -----
  // For a given party type, return [debit_type_to_reduce, credit_type_to_reduce]
  // Reducing a "payable" (Bepaari/Loan/Amanat/Capital we owe) = Debit that party
  // Reducing a "receivable" (Dukandar/Advance they owe us) = Credit that party
  const PARTY_KIND = {
    BEPAARI: "PAYABLE",     // we owe → reduce by Debit
    DUKANDAR: "RECEIVABLE", // they owe → reduce by Credit
    ADVANCE: "RECEIVABLE",
    CAPITAL: "PAYABLE",
    LOAN: "PAYABLE",
    AMANAT: "PAYABLE",
  };
  // What does "reduce" mean for this party type? Returns 'debit' or 'credit'
  const reduceSide = (ptype) => PARTY_KIND[ptype] === "RECEIVABLE" ? "credit" : "debit";
  const increaseSide = (ptype) => reduceSide(ptype) === "credit" ? "debit" : "credit";

  // Build party options including current balance label
  const partyOptionsWithBal = (ptype) => {
    const list = (ptype === "BEPAARI" ? bepaaris
      : ptype === "DUKANDAR" ? dukandars
      : ptype === "ADVANCE" ? advanceParties
      : capitalPartners.filter(p => p.partner_type === ptype)) || [];
    return list.map(p => {
      const bal = balanceMap[`${ptype}:${p.id}`] || 0;
      const balStr = bal !== 0 ? ` (${formatCurrency(Math.abs(bal))}${bal > 0 ? '' : ' adv'})` : '';
      return { value: p.id, label: `${p.name}${balStr}` };
    }).sort((a, b) => a.label.localeCompare(b.label));
  };

  const getQuickPartyBalance = (ptype, id) => {
    if (!ptype || !id) return null;
    return balanceMap[`${ptype}:${id}`] ?? null;
  };

  // Translate Quick Mode selection into a standard adjustment payload
  const buildQuickPayload = () => {
    const amount = parseFloat(quick.amount || quick.cash_amount || 0);
    if (!(amount > 0)) {
      alert("Enter a valid amount");
      return null;
    }
    let payload = { date: form.date, amount, narration: quick.narration || form.narration || "" };
    if (quickMode === "MOVE") {
      if (!quick.reduce_type || !quick.reduce_party_id || !quick.increase_type || !quick.increase_party_id) {
        alert("Pick both parties (reduce-from and increase-to)");
        return null;
      }
      // Same-type transfers only (Bepaari→Bepaari, Dukandar→Dukandar, Advance→Advance).
      // The backend ledger logic now correctly handles these when encoded as:
      // debit_party = the party whose balance is REDUCED
      // credit_party = the party whose balance is INCREASED
      // (this is the universal rule for same-type transfers regardless of payable/receivable kind)
      if (quick.reduce_type !== quick.increase_type) {
        alert("Move between different party types is not allowed here. For cross-type adjustments (e.g. Dukandar pays Bepaari), use the Advanced (Dr/Cr) mode below.");
        return null;
      }
      payload = { ...payload,
        debit_type: quick.reduce_type, debit_party_id: quick.reduce_party_id,
        credit_type: quick.increase_type, credit_party_id: quick.increase_party_id };
    } else if (quickMode === "WRITEOFF") {
      if (!quick.reduce_type || !quick.reduce_party_id) { alert("Pick a party to reduce"); return null; }
      if (!quick.offset_head) { alert("Pick offset head"); return null; }
      const rs = reduceSide(quick.reduce_type);
      if (rs === "debit") {
        // Reduce payable: Debit party, Credit offset head
        payload = { ...payload, debit_type: quick.reduce_type, debit_party_id: quick.reduce_party_id, credit_type: quick.offset_head, credit_party_id: `__${quick.offset_head}__` };
      } else {
        // Reduce receivable: Credit party, Debit offset head
        payload = { ...payload, debit_type: quick.offset_head, debit_party_id: `__${quick.offset_head}__`, credit_type: quick.reduce_type, credit_party_id: quick.reduce_party_id };
      }
    } else if (quickMode === "CASH_DIFF") {
      const cashHead = quick.cash_mode || "CASH";
      const offset = quick.offset_head || "MANDI_EXPENSE";
      if (quick.cash_direction === "INCREASE") {
        // Cash in hand > app → app cash should go up. Debit CASH, Credit offset (income head reduces, or expense reverses)
        payload = { ...payload, debit_type: cashHead, debit_party_id: `__${cashHead}__`, credit_type: offset, credit_party_id: `__${offset}__` };
      } else {
        // Cash in hand < app → app cash should go down. Credit CASH, Debit offset (expense increases)
        payload = { ...payload, debit_type: offset, debit_party_id: `__${offset}__`, credit_type: cashHead, credit_party_id: `__${cashHead}__` };
      }
    } else if (quickMode === "PROVISION") {
      // Provision: Debit CAPITAL/COMMISSION (source), Credit ZAKAT/etc (liability head)
      if (!quick.provision_source_type) { alert("Pick provision source"); return null; }
      if (!quick.provision_target_head) { alert("Pick provision target head"); return null; }
      if (quick.provision_source_type === "CAPITAL") {
        if (!quick.provision_source_party_id) { alert("Pick capital partner"); return null; }
        payload = { ...payload, debit_type: "CAPITAL", debit_party_id: quick.provision_source_party_id, credit_type: quick.provision_target_head, credit_party_id: `__${quick.provision_target_head}__` };
      } else {
        // Source is an income head like COMMISSION
        payload = { ...payload, debit_type: quick.provision_source_type, debit_party_id: `__${quick.provision_source_type}__`, credit_type: quick.provision_target_head, credit_party_id: `__${quick.provision_target_head}__` };
      }
    }
    return payload;
  };

  const handleQuickSubmit = async () => {
    const payload = buildQuickPayload();
    if (!payload) return;
    try {
      await axios.post(`${API}/adjustments`, payload);
      setQuick({ reduce_type: "", reduce_party_id: "", increase_type: "", increase_party_id: "",
        offset_head: "MANDI_EXPENSE", cash_direction: "INCREASE", cash_mode: "CASH",
        amount: "", narration: "", provision_source_type: "", provision_target_head: "ZAKAT", provision_source_party_id: "" });
      fetchData();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.debit_type || !form.credit_type) {
      alert("Please select both Debit and Credit types");
      return;
    }
    if (!isExpenseHead(form.debit_type) && !form.debit_party_id) {
      alert("Please select a Debit party");
      return;
    }
    if (!isExpenseHead(form.credit_type) && !form.credit_party_id) {
      alert("Please select a Credit party");
      return;
    }
    await axios.post(`${API}/adjustments`, {
      ...form,
      amount: parseFloat(form.amount),
      debit_party_id: isExpenseHead(form.debit_type) ? `__${form.debit_type}__` : form.debit_party_id,
      credit_party_id: isExpenseHead(form.credit_type) ? `__${form.credit_type}__` : form.credit_party_id
    });
    setForm({
      ...form,
      debit_type: "",
      debit_party_id: "",
      credit_type: "",
      credit_party_id: "",
      amount: "",
      narration: ""
    });
    fetchData();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this adjustment?")) {
      await axios.delete(`${API}/adjustments/${id}`);
      fetchData();
    }
  };

  const handleEdit = (adj) => {
    setEditItem(adj);
    setEditForm({
      date: adj.date,
      amount: adj.amount,
      narration: adj.narration || ""
    });
  };

  const handleEditSave = async () => {
    await axios.put(`${API}/adjustments/${editItem.id}`, {
      ...editForm,
      amount: parseFloat(editForm.amount)
    });
    setEditItem(null);
    fetchData();
  };

  // Sorting
  const sortedAdjustments = [...adjustments].sort((a, b) => {
    if (sortBy === "date-desc") return b.date.localeCompare(a.date);
    if (sortBy === "date-asc") return a.date.localeCompare(b.date);
    if (sortBy === "amount-desc") return b.amount - a.amount;
    if (sortBy === "amount-asc") return a.amount - b.amount;
    return 0;
  });

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page">
      <h2>Adjustments / Journal Voucher</h2>
      <p className="hint">Make simple adjustments — move balance between parties, write off small balances, correct cash difference, or set aside provisions.</p>

      {/* === QUICK MODE selector === */}
      <div style={{
        background: '#fffbeb',
        border: '1px solid #fbbf24',
        borderRadius: '10px',
        padding: '14px 16px',
        marginBottom: '16px',
      }}>
        <div style={{display:'flex', gap:'8px', flexWrap:'wrap', marginBottom: '12px'}}>
          {[
            { key: 'MOVE', icon: '📤', label: 'Move balance between parties' },
            { key: 'WRITEOFF', icon: '✂️', label: 'Write off party balance' },
            { key: 'CASH_DIFF', icon: '💰', label: 'Cash difference (App vs Hand)' },
            { key: 'PROVISION', icon: '📊', label: 'Provision (Zakat etc)' },
            { key: 'ADVANCED', icon: '⚙️', label: 'Advanced (Dr/Cr)' },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              data-testid={`jv-quick-${opt.key.toLowerCase()}`}
              onClick={() => setQuickMode(opt.key)}
              style={{
                background: quickMode === opt.key ? '#1f2937' : '#fff',
                color: quickMode === opt.key ? '#fbbf24' : '#475569',
                border: quickMode === opt.key ? '2px solid #1f2937' : '1px solid #e2e8f0',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >{opt.icon} {opt.label}</button>
          ))}
        </div>

        {quickMode !== 'ADVANCED' && (
          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
              <label style={{fontSize:'12.5px', fontWeight:600, color:'#475569'}}>Date:</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>

            {quickMode === 'MOVE' && (
              <>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, color:'#dc2626', minWidth:'140px'}}>Reduce balance of:</label>
                  <select value={quick.reduce_type} onChange={(e) => setQuick({ ...quick, reduce_type: e.target.value, reduce_party_id: "" })}>
                    <option value="">Type...</option>
                    <option value="BEPAARI">Bepaari</option>
                    <option value="DUKANDAR">Dukandar</option>
                    <option value="ADVANCE">Advance Party</option>
                  </select>
                  {quick.reduce_type && (
                    <SearchableSelect
                      options={partyOptionsWithBal(quick.reduce_type)}
                      value={quick.reduce_party_id}
                      onChange={(val) => setQuick({ ...quick, reduce_party_id: val })}
                      placeholder="Pick party"
                      testId="jv-q-reduce-party"
                    />
                  )}
                  {quick.reduce_party_id && (() => {
                    const b = getQuickPartyBalance(quick.reduce_type, quick.reduce_party_id);
                    return b != null && (
                      <span style={{fontSize:'12px', color:'#64748b'}}>Current: <strong>{formatCurrency(Math.abs(b))}</strong></span>
                    );
                  })()}
                </div>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, color:'#16a34a', minWidth:'140px'}}>Add to balance of:</label>
                  <select value={quick.increase_type} onChange={(e) => setQuick({ ...quick, increase_type: e.target.value, increase_party_id: "" })}>
                    <option value="">Type...</option>
                    <option value="BEPAARI">Bepaari</option>
                    <option value="DUKANDAR">Dukandar</option>
                    <option value="ADVANCE">Advance Party</option>
                  </select>
                  {quick.increase_type && (
                    <SearchableSelect
                      options={partyOptionsWithBal(quick.increase_type)}
                      value={quick.increase_party_id}
                      onChange={(val) => setQuick({ ...quick, increase_party_id: val })}
                      placeholder="Pick party"
                      testId="jv-q-increase-party"
                    />
                  )}
                  {quick.increase_party_id && (() => {
                    const b = getQuickPartyBalance(quick.increase_type, quick.increase_party_id);
                    return b != null && (
                      <span style={{fontSize:'12px', color:'#64748b'}}>Current: <strong>{formatCurrency(Math.abs(b))}</strong></span>
                    );
                  })()}
                </div>
              </>
            )}

            {quickMode === 'WRITEOFF' && (
              <>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, color:'#dc2626', minWidth:'140px'}}>Reduce balance of:</label>
                  <select value={quick.reduce_type} onChange={(e) => setQuick({ ...quick, reduce_type: e.target.value, reduce_party_id: "" })}>
                    <option value="">Type...</option>
                    <option value="BEPAARI">Bepaari</option>
                    <option value="DUKANDAR">Dukandar</option>
                    <option value="ADVANCE">Advance Party</option>
                  </select>
                  {quick.reduce_type && (
                    <SearchableSelect
                      options={partyOptionsWithBal(quick.reduce_type)}
                      value={quick.reduce_party_id}
                      onChange={(val) => setQuick({ ...quick, reduce_party_id: val })}
                      placeholder="Pick party"
                      testId="jv-q-writeoff-party"
                    />
                  )}
                  {quick.reduce_party_id && (() => {
                    const b = getQuickPartyBalance(quick.reduce_type, quick.reduce_party_id);
                    return b != null && (
                      <span style={{fontSize:'12px', color:'#64748b'}}>Current: <strong>{formatCurrency(Math.abs(b))}</strong></span>
                    );
                  })()}
                </div>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, color:'#475569', minWidth:'140px'}}>Offset against:</label>
                  <select value={quick.offset_head} onChange={(e) => setQuick({ ...quick, offset_head: e.target.value })} data-testid="jv-q-writeoff-head">
                    <option value="MANDI_EXPENSE">Mandi Expense</option>
                    <option value="BF_DISCOUNT">BF Discount</option>
                  </select>
                </div>
              </>
            )}

            {quickMode === 'CASH_DIFF' && (
              <>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, minWidth:'140px'}}>App vs Hand:</label>
                  <select value={quick.cash_direction} onChange={(e) => setQuick({ ...quick, cash_direction: e.target.value })} data-testid="jv-q-cash-dir">
                    <option value="INCREASE">Cash in hand &gt; App (Increase app cash)</option>
                    <option value="DECREASE">Cash in hand &lt; App (Decrease app cash)</option>
                  </select>
                </div>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, minWidth:'140px'}}>Money type:</label>
                  <select value={quick.cash_mode} onChange={(e) => setQuick({ ...quick, cash_mode: e.target.value })}>
                    <option value="CASH">Cash</option>
                    <option value="BANK">Bank</option>
                  </select>
                </div>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, minWidth:'140px'}}>Offset against:</label>
                  <select value={quick.offset_head} onChange={(e) => setQuick({ ...quick, offset_head: e.target.value })}>
                    <option value="MANDI_EXPENSE">Mandi Expense</option>
                    <option value="BF_DISCOUNT">BF Discount</option>
                  </select>
                </div>
              </>
            )}

            {quickMode === 'PROVISION' && (
              <>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, color:'#dc2626', minWidth:'140px'}}>Source (reduce):</label>
                  <select value={quick.provision_source_type || ""} onChange={(e) => setQuick({ ...quick, provision_source_type: e.target.value, provision_source_party_id: "" })}>
                    <option value="">Type...</option>
                    <option value="CAPITAL">Capital Partner</option>
                    <option value="COMMISSION">Commission Earned</option>
                    <option value="KK">KK</option>
                    <option value="JB">JB</option>
                  </select>
                  {quick.provision_source_type === "CAPITAL" && (
                    <SearchableSelect
                      options={capitalPartners.filter(p => p.partner_type === "CAPITAL").map(p => ({ value: p.id, label: p.name }))}
                      value={quick.provision_source_party_id || ""}
                      onChange={(val) => setQuick({ ...quick, provision_source_party_id: val })}
                      placeholder="Pick partner"
                      testId="jv-q-prov-partner"
                    />
                  )}
                </div>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, color:'#16a34a', minWidth:'140px'}}>Provision for:</label>
                  <select value={quick.provision_target_head || "ZAKAT"} onChange={(e) => setQuick({ ...quick, provision_target_head: e.target.value })} data-testid="jv-q-prov-target">
                    <option value="ZAKAT">Zakat</option>
                    <option value="MANDI_EXPENSE">Mandi Expense (pre-provision)</option>
                    <option value="MHN_PERSONAL">MHN Personal Draw (pre-provision)</option>
                  </select>
                </div>
              </>
            )}

            {quickMode !== 'ADVANCED' && (
              <>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, minWidth:'140px'}}>Amount:</label>
                  <input type="number" placeholder="₹" value={quick.amount || ""} onChange={(e) => setQuick({ ...quick, amount: e.target.value })} style={{maxWidth:'160px'}} data-testid="jv-q-amount" />
                </div>
                <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
                  <label style={{fontSize:'12.5px', fontWeight:600, minWidth:'140px'}}>Reason (optional):</label>
                  <input type="text" placeholder="Brief note" value={quick.narration || ""} onChange={(e) => setQuick({ ...quick, narration: e.target.value })} style={{minWidth:'260px'}} />
                </div>
                <div>
                  <button type="button" onClick={handleQuickSubmit} className="btn-primary" data-testid="jv-q-save">Save Adjustment</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* === ADVANCED MODE (original Dr/Cr form) === */}
      {quickMode === 'ADVANCED' && (
      <form className="entry-form adjustment-form" onSubmit={handleSubmit}>
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        
        <div className="jv-row">
          <div className="jv-section debit-section">
            <label>DEBIT</label>
            <select value={form.debit_type} onChange={(e) => setForm({ ...form, debit_type: e.target.value, debit_party_id: "" })} required>
              <option value="">Select Type</option>
              <optgroup label="Parties">
                <option value="DUKANDAR">Dukandar</option>
                <option value="ADVANCE">Advance Party</option>
                <option value="BEPAARI">Bepaari</option>
                <option value="CAPITAL">Capital</option>
                <option value="LOAN">Loan</option>
                <option value="AMANAT">Amanat</option>
              </optgroup>
              <optgroup label="Expense Heads (Write-off)">
                <option value="MANDI_EXPENSE">Mandi Expense</option>
                <option value="BF_DISCOUNT">BF Discount</option>
                <option value="MHN_PERSONAL">MHN Personal Draw</option>
              </optgroup>
              <optgroup label="Income Heads (Reduce / Withdraw)">
                <option value="COMMISSION">Commission</option>
                <option value="KK">KK</option>
                <option value="JB">JB</option>
                <option value="ZAKAT">Zakat</option>
              </optgroup>
              <optgroup label="Cash / Bank (Money In)">
                <option value="CASH">Cash (Received)</option>
                <option value="BANK">Bank (Received)</option>
              </optgroup>
            </select>
            {!isExpenseHead(form.debit_type) && (
              <SearchableSelect
                options={getParties(form.debit_type).map(p => ({ value: p.id, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
                value={form.debit_party_id}
                onChange={(val) => setForm({ ...form, debit_party_id: val })}
                placeholder="Select Party"
                disabled={!form.debit_type}
                testId="jv-debit-party"
              />
            )}
          </div>

          <div className="jv-section credit-section">
            <label>CREDIT</label>
            <select value={form.credit_type} onChange={(e) => setForm({ ...form, credit_type: e.target.value, credit_party_id: "" })} required>
              <option value="">Select Type</option>
              <optgroup label="Parties">
                <option value="BEPAARI">Bepaari</option>
                <option value="DUKANDAR">Dukandar</option>
                <option value="ADVANCE">Advance Party</option>
                <option value="CAPITAL">Capital</option>
                <option value="LOAN">Loan</option>
                <option value="AMANAT">Amanat</option>
              </optgroup>
              <optgroup label="Expense Heads (Write-off)">
                <option value="MANDI_EXPENSE">Mandi Expense</option>
                <option value="BF_DISCOUNT">BF Discount</option>
                <option value="MHN_PERSONAL">MHN Personal Draw</option>
              </optgroup>
              <optgroup label="Income Heads (Add / Receive)">
                <option value="COMMISSION">Commission</option>
                <option value="KK">KK</option>
                <option value="JB">JB</option>
                <option value="ZAKAT">Zakat</option>
              </optgroup>
              <optgroup label="Cash / Bank (Money Out)">
                <option value="CASH">Cash (Paid Out)</option>
                <option value="BANK">Bank (Paid Out)</option>
              </optgroup>
            </select>
            {!isExpenseHead(form.credit_type) && (
              <SearchableSelect
                options={getParties(form.credit_type).map(p => ({ value: p.id, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
                value={form.credit_party_id}
                onChange={(val) => setForm({ ...form, credit_party_id: val })}
                placeholder="Select Party"
                disabled={!form.credit_type}
                testId="jv-credit-party"
              />
            )}
          </div>
        </div>

        <input type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required min="1" />
        <input type="text" placeholder="Narration (e.g., Jagdish paid Bepaari directly)" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} style={{minWidth: '300px'}} />
        <button type="submit" className="btn-primary">Add Adjustment</button>
      </form>
      )}

      <div className="filter-bar">
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
          <option value="date-desc">Date (Newest)</option>
          <option value="date-asc">Date (Oldest)</option>
          <option value="amount-desc">Amount (High-Low)</option>
          <option value="amount-asc">Amount (Low-High)</option>
        </select>
      </div>

      <div className="table-container desktop-only">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Debit (Who Paid)</th>
              <th>Credit (Who Received)</th>
              <th>Amount</th>
              <th>Narration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAdjustments.map((a) => (
              <tr key={a.id}>
                <td>{fmtDate(a.date)}</td>
                <td><span className="badge debit">{a.debit_type}</span> {a.debit_party_name}</td>
                <td><span className="badge credit">{a.credit_type}</span> {a.credit_party_name}</td>
                <td>{formatCurrency(a.amount)}</td>
                <td>{a.narration || "-"}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleEdit(a)}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(a.id)}>X</button>
                </td>
              </tr>
            ))}
            {adjustments.length === 0 && (
              <tr><td colSpan="6" style={{textAlign: 'center', color: '#888'}}>No adjustments recorded yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards mobile-only">
        {sortedAdjustments.map((a) => (
          <div key={a.id} className="mobile-entry-card" data-testid="adj-card">
            <div className="mec-header">
              <span className="mec-date">{fmtDate(a.date)}</span>
              <span className="mec-net">{formatCurrency(a.amount)}</span>
            </div>
            <div className="jv-leg">
              <span className="badge debit">DEBIT · {a.debit_type}</span>
              <strong>{a.debit_party_name}</strong>
            </div>
            <div className="jv-leg">
              <span className="badge credit">CREDIT · {a.credit_type}</span>
              <strong>{a.credit_party_name}</strong>
            </div>
            {a.narration && (
              <div className="mec-comment">
                <span className="mec-comment-label">Note:</span> {a.narration}
              </div>
            )}
            <div className="mec-actions">
              <button className="btn-edit" onClick={() => handleEdit(a)}>Edit</button>
              <button className="btn-delete" onClick={() => handleDelete(a.id)}>X</button>
            </div>
          </div>
        ))}
        {adjustments.length === 0 && (
          <div className="no-data">No adjustments recorded yet</div>
        )}
      </div>

      {/* Edit Modal */}
      {editItem && (
        <div className="modal-overlay" onClick={() => setEditItem(null)}>
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <h3>Edit Adjustment</h3>
            <div className="edit-form">
              <label>Date:<input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} /></label>
              <label>Amount:<input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} /></label>
              <label>Narration:<input type="text" value={editForm.narration} onChange={(e) => setEditForm({ ...editForm, narration: e.target.value })} /></label>
            </div>
            <div className="modal-actions">
              <button className="btn-clear" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== BALANCE TRANSFER ==============
const BalanceTransfer = () => {
  const [bepaaris, setBeparis] = useState([]);
  const [dukandars, setDukandars] = useState([]);
  const [advanceParties, setAdvanceParties] = useState([]);
  const [cashBookEntries, setCashBookEntries] = useState([]);
  const [pastTransfers, setPastTransfers] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    from_type: "DUKANDAR",
    from_party_id: "",
    to_type: "DUKANDAR",
    to_party_id: "",
    to_party_name: "",
    amount: "",
    narration: "",
    create_new: false
  });
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const fetchData = async () => {
    try {
      const [bRes, dRes, aRes, tRes] = await Promise.all([
        axios.get(`${API}/bepaaris`),
        axios.get(`${API}/dukandars`),
        axios.get(`${API}/advance-parties`),
        axios.get(`${API}/balance-transfers`)
      ]);
      setBeparis(bRes.data);
      setDukandars(dRes.data);
      setAdvanceParties(aRes.data);
      setPastTransfers(tRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Fetch cash book entries when source party changes
  useEffect(() => {
    if (form.from_party_id) {
      axios.get(`${API}/cash-book`).then(res => {
        const filtered = res.data.filter(e => e.party_id === form.from_party_id);
        setCashBookEntries(filtered);
      });
    } else {
      setCashBookEntries([]);
    }
    setSelectedEntries([]);
  }, [form.from_party_id]);

  const getParties = (type) => {
    if (type === "BEPAARI") return bepaaris;
    if (type === "DUKANDAR") return dukandars;
    if (type === "ADVANCE") return advanceParties;
    return [];
  };

  const getSourcePartyBalance = () => {
    const parties = getParties(form.from_type);
    const party = parties.find(p => p.id === form.from_party_id);
    return party?.opening_balance || 0;
  };

  const handleTransfer = async () => {
    if (!form.from_party_id || !form.amount) {
      alert("Please select source party and enter amount");
      return;
    }
    if (!form.create_new && !form.to_party_id) {
      alert("Please select destination party or check 'Create New'");
      return;
    }
    if (form.create_new && !form.to_party_name) {
      alert("Please enter name for new party");
      return;
    }

    try {
      // Step 1: Transfer balance
      const transferRes = await axios.post(`${API}/balance-transfer`, {
        from_type: form.from_type,
        from_party_id: form.from_party_id,
        to_type: form.to_type,
        to_party_id: form.create_new ? null : form.to_party_id,
        to_party_name: form.create_new ? form.to_party_name : null,
        amount: parseFloat(form.amount),
        date: form.date,
        narration: form.narration
      });

      // Step 2: Reassign selected cash book entries
      if (selectedEntries.length > 0) {
        await axios.put(`${API}/cash-book/reassign`, {
          entry_ids: selectedEntries,
          new_party_id: transferRes.data.new_to_party_id || form.to_party_id,
          new_party_type: form.to_type
        });
      }

      setMessage(`✅ Transferred ₹${formatCurrency(form.amount)} from ${transferRes.data.from_party} to ${transferRes.data.to_party}. ${selectedEntries.length > 0 ? `Also moved ${selectedEntries.length} cash book entries.` : ''}`);
      
      // Reset form
      setForm({ ...form, from_party_id: "", to_party_id: "", to_party_name: "", amount: "", narration: "", create_new: false });
      setSelectedEntries([]);
      fetchData();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const toggleEntry = (id) => {
    setSelectedEntries(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page">
      <h2>Balance Transfer</h2>
      <p className="hint">Transfer opening balance from one party to another. Use this to correct party splits or move balances to new parties.</p>

      {message && <div className="success-message">{message}</div>}

      <div className="transfer-container">
        <div className="transfer-date-row">
          <label>Transaction Date:</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div className="transfer-section from-section">
          <h3>FROM (Source Party)</h3>
          <select value={form.from_type} onChange={(e) => setForm({ ...form, from_type: e.target.value, from_party_id: "" })}>
            <option value="DUKANDAR">Dukandar</option>
            <option value="BEPAARI">Bepaari</option>
            <option value="ADVANCE">Advance Party</option>
          </select>
          <SearchableSelect
            options={getParties(form.from_type).map(p => ({ value: p.id, label: `${p.name} (₹${p.opening_balance?.toLocaleString() || 0})` })).sort((a, b) => a.label.localeCompare(b.label))}
            value={form.from_party_id}
            onChange={(val) => setForm({ ...form, from_party_id: val })}
            placeholder="Select Party"
            testId="bt-from-party"
          />
          {form.from_party_id && (
            <div className="balance-info">Current Opening Balance: <strong>₹{getSourcePartyBalance().toLocaleString()}</strong></div>
          )}
        </div>

        <div className="transfer-arrow">→</div>

        <div className="transfer-section to-section">
          <h3>TO (Destination Party)</h3>
          <select value={form.to_type} onChange={(e) => setForm({ ...form, to_type: e.target.value, to_party_id: "" })}>
            <option value="DUKANDAR">Dukandar</option>
            <option value="BEPAARI">Bepaari</option>
            <option value="ADVANCE">Advance Party</option>
          </select>
          
          <label className="checkbox-label">
            <input type="checkbox" checked={form.create_new} onChange={(e) => setForm({ ...form, create_new: e.target.checked, to_party_id: "" })} />
            Create New Party
          </label>

          {form.create_new ? (
            <input type="text" placeholder="New Party Name" value={form.to_party_name} onChange={(e) => setForm({ ...form, to_party_name: e.target.value })} />
          ) : (
            <SearchableSelect
              options={getParties(form.to_type).map(p => ({ value: p.id, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
              value={form.to_party_id}
              onChange={(val) => setForm({ ...form, to_party_id: val })}
              placeholder="Select Existing Party"
              testId="bt-to-party"
            />
          )}
        </div>
      </div>

      <div className="amount-section">
        <label>Amount to Transfer:</label>
        <input type="number" placeholder="Enter amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="amount-input" />
      </div>

      <div className="narration-section">
        <label>Comment / Narration (Optional):</label>
        <input type="text" placeholder="Brief description of transfer" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="narration-input" />
      </div>

      {cashBookEntries.length > 0 && (
        <div className="entries-section">
          <h3>Also Move These Cash & Bank Entries? (Optional)</h3>
          <p className="hint">Select entries that should be reassigned to the destination party</p>
          <div className="entries-list">
            {cashBookEntries.map(e => (
              <label key={e.id} className="entry-checkbox">
                <input type="checkbox" checked={selectedEntries.includes(e.id)} onChange={() => toggleEntry(e.id)} />
                <span>{e.date} | {e.sub_type} | ₹{e.amount.toLocaleString()} | {e.mode}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <button className="btn-primary btn-transfer" onClick={handleTransfer}>Transfer Balance</button>

      {/* Past Balance Transfers - editable / removable */}
      <div className="entries-section" style={{marginTop: '32px'}}>
        <h3>Past Balance Transfers</h3>
        <p className="hint">Use ❌ to delete an entry without touching balances (when you've already corrected them in Masters). Use ↩️ to UNDO completely (reverses the opening-balance changes).</p>
        {pastTransfers.length === 0 ? (
          <p className="hint" style={{fontStyle:'italic'}}>No past balance transfers.</p>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table className="data-table" data-testid="past-transfers-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>From</th>
                  <th>To</th>
                  <th style={{textAlign:'right'}}>Amount</th>
                  <th>Narration</th>
                  <th style={{textAlign:'center'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pastTransfers.map(t => (
                  <tr key={t.id}>
                    <td>{fmtDate(t.date)}</td>
                    <td>{t.from_party_name}</td>
                    <td>{t.to_party_name}</td>
                    <td style={{textAlign:'right'}}>{formatCurrency(t.amount)}</td>
                    <td>{t.narration || '-'}</td>
                    <td style={{textAlign:'center', whiteSpace:'nowrap'}}>
                      <button
                        data-testid={`bt-delete-record-${t.id}`}
                        className="btn-delete"
                        title="Delete record only (opening balances stay as they are now)"
                        onClick={async () => {
                          if (!window.confirm(`Remove this transfer record?\n\n${t.from_party_name} → ${t.to_party_name} ₹${t.amount.toLocaleString()}\n\nThis ONLY removes the audit record. Opening balances of both parties stay AS THEY ARE NOW. Use this if you've already corrected the balances manually in Masters.`)) return;
                          try {
                            await axios.delete(`${API}/balance-transfer/${t.id}?reverse=false`);
                            setMessage(`✅ Transfer record removed.`);
                            fetchData();
                          } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
                        }}
                        style={{marginRight: '6px'}}
                      >❌ Remove</button>
                      <button
                        data-testid={`bt-undo-${t.id}`}
                        className="btn-secondary"
                        title="Undo: reverses the opening balance changes that this transfer made"
                        onClick={async () => {
                          if (!window.confirm(`UNDO this transfer completely?\n\n${t.from_party_name} → ${t.to_party_name} ₹${t.amount.toLocaleString()}\n\nThis will REVERSE the opening balance changes (add ₹${t.amount.toLocaleString()} back to ${t.from_party_name}, subtract from ${t.to_party_name}) AND remove the record. Only use this if you have NOT yet manually adjusted the Masters opening balances.`)) return;
                          try {
                            await axios.delete(`${API}/balance-transfer/${t.id}?reverse=true`);
                            setMessage(`✅ Transfer UNDONE (opening balances reversed).`);
                            fetchData();
                          } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
                        }}
                      >↩️ Undo</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== PARTY STATEMENT (NEW!) ==============
const PartyStatement = () => {
  const [searchParams] = useSearchParams();
  const [bepaaris, setBeparis] = useState([]);
  const [dukandars, setDukandars] = useState([]);
  const [advanceParties, setAdvanceParties] = useState([]);
  const [settings, setSettings] = useState({ commission_rate: 4, jb_rate: 10, kk_fixed: 100 });
  const [partyType, setPartyType] = useState(searchParams.get("type") || "bepaari");
  const [partyId, setPartyId] = useState(searchParams.get("id") || "");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoFetched, setAutoFetched] = useState(false);
  const [agingData, setAgingData] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/bepaaris`),
      axios.get(`${API}/dukandars`),
      axios.get(`${API}/advance-parties`),
      axios.get(`${API}/settings`)
    ]).then(([b, d, a, s]) => {
      setBeparis(b.data);
      setDukandars(d.data);
      setAdvanceParties(a.data || []);
      setSettings(s.data || {});
    });
  }, []);

  // Auto-fetch when coming from ledger with URL params
  useEffect(() => {
    if (partyId && !autoFetched && (bepaaris.length > 0 || dukandars.length > 0)) {
      setAutoFetched(true);
      fetchStatement();
    }
  }, [partyId, bepaaris, dukandars]);

  const fetchStatement = async () => {
    if (!partyId) { alert("Please select a party"); return; }
    setLoading(true);
    const endpoint = partyType === "advance"
      ? `${API}/advance-party-statement/${partyId}`
      : `${API}/party-statement/${partyType}/${partyId}`;
    let url = `${endpoint}?`;
    if (fromDate) url += `from_date=${fromDate}&`;
    if (toDate) url += `to_date=${toDate}&`;
    const res = await axios.get(url);
    setStatement(res.data);
    setLoading(false);
    // Fetch aging for dukandars
    if (partyType === "dukandar" && partyId) {
      axios.get(`${API}/payment-aging/${partyId}`).then(r => setAgingData(r.data)).catch(() => setAgingData(null));
    } else {
      setAgingData(null);
    }
  };

  const downloadExcel = () => {
    if (partyType === "advance") {
      alert("Excel export for Advance Party statements not yet supported. Use Print/Save PDF for now.");
      return;
    }
    let url = `${API}/export/party-statement/${partyType}/${partyId}?`;
    if (fromDate) url += `from_date=${fromDate}&`;
    if (toDate) url += `to_date=${toDate}&`;
    window.open(url, '_blank');
  };

  // Build ledger entries with running balance
  const buildLedgerEntries = () => {
    if (!statement) return [];
    
    const entries = [];
    const isBepari = partyType === "bepaari";
    const isAdvance = partyType === "advance";

    // === ADVANCE PARTY LEDGER ===
    if (isAdvance) {
      // Cash entries: GIVEN=Debit (increases receivable), RECEIVED=Credit (reduces it)
      statement.cash_entries.forEach(c => {
        const isGiven = c.sub_type === "GIVEN";
        entries.push({
          date: c.date,
          description: `${c.sub_type} (${c.mode})${c.particulars ? ' - ' + c.particulars : ''}`,
          debit: isGiven ? c.amount : 0,
          credit: !isGiven ? c.amount : 0,
          type: 'cash',
        });
      });
      // JVs: backend already returns ledger_debit/ledger_credit + effect label
      (statement.adjustments || []).forEach(a => {
        entries.push({
          date: a.date,
          description: `JV: ${a.effect}${a.narration ? ' — ' + a.narration : ''}`,
          debit: a.ledger_debit || 0,
          credit: a.ledger_credit || 0,
          type: 'adjustment',
        });
      });
      entries.sort((a, b) => a.date.localeCompare(b.date));
      let balance = statement.summary.opening_balance || 0;
      entries.forEach(e => {
        // Advance party: positive balance = they owe us (receivable). Debit increases, Credit reduces.
        balance = balance + e.debit - e.credit;
        e.balance = balance;
      });
      return entries;
    }
    
    if (isBepari) {
      // BEPAARI LEDGER: Group sales by date, show NET amount only (like Aakda)
      // NET = Gross Sales - Commission - JB - KK - Motor - Bhussa - Gawali - CashAdv
      // DON'T show Dukandar names or individual expenses
      
      // Group sales by date
      const salesByDate = {};
      statement.sales.forEach(s => {
        if (!salesByDate[s.date]) {
          salesByDate[s.date] = { qty: 0, gross: 0 };
        }
        salesByDate[s.date].qty += s.quantity;
        salesByDate[s.date].gross += s.gross_amount;
      });
      
      // Group expenses by date for display only (not used in net calc — they show as separate dated lines)
      
      // Calculate commission, JB, KK per day using actual settings + per-bepaari overrides
      // Mirrors backend logic in /api/bepaari-ledger (server.py line 901-908)
      const selectedBepaari = bepaaris.find(b => b.id === partyId) || {};
      const flatRate = selectedBepaari.flat_rate_per_goat; // per-goat flat overrides %
      const commissionRate = selectedBepaari.commission_percent != null
        ? selectedBepaari.commission_percent
        : (settings.commission_rate ?? 4);
      const jbRate = selectedBepaari.jb_rate_override != null
        ? selectedBepaari.jb_rate_override
        : (settings.jb_rate ?? 10);
      const kkFixed = settings.kk_fixed ?? 100;

      // Group same-date Motor/Bhussa/Gawali expenses so they net into the Sales line
      // (Cash Advances and Payments stay as their own dated lines — they're not "deductions on sale")
      const SAME_DAY_DEDUCTIONS = ["MOTOR", "BHUSSA", "GAWALI"];
      const expensesByDate = {};
      statement.cash_entries.forEach(c => {
        if (SAME_DAY_DEDUCTIONS.includes(c.sub_type)) {
          if (!expensesByDate[c.date]) expensesByDate[c.date] = { motor: 0, bhussa: 0, gawali: 0, total: 0 };
          const key = c.sub_type.toLowerCase();
          expensesByDate[c.date][key] += c.amount;
          expensesByDate[c.date].total += c.amount;
        }
      });

      // Add NET sales entries per date (Comm/JB/KK + same-day Motor/Bhussa/Gawali netted off)
      // Track which dates had a sale so we can distinguish "same-day deduction" (netted) from
      // "deduction on a non-sale date" (kept as a separate debit line)
      const saleDates = new Set(Object.keys(salesByDate));
      Object.keys(salesByDate).sort().forEach(date => {
        const s = salesByDate[date];
        const commission = flatRate
          ? s.qty * flatRate
          : s.gross * (commissionRate / 100);
        const jb = s.qty * jbRate;
        const kk = kkFixed; // Per market day
        const exp = expensesByDate[date] || { motor: 0, bhussa: 0, gawali: 0, total: 0 };
        const netAmount = s.gross - commission - jb - kk - exp.total;

        // Build a clean inclusive description showing what was netted
        const deductedHeads = ["Comm", "JB", "KK"];
        if (exp.motor > 0) deductedHeads.push("Motor");
        if (exp.gawali > 0) deductedHeads.push("Gawali");
        if (exp.bhussa > 0) deductedHeads.push("Bhussa");

        // Inclusive sub-text: small grey breakdown so audit detail is preserved
        const inclParts = [
          `Comm ${formatCurrency(commission)}`,
          `JB ${formatCurrency(jb)}`,
          `KK ${formatCurrency(kk)}`,
        ];
        if (exp.motor > 0) inclParts.push(`Motor ${formatCurrency(exp.motor)}`);
        if (exp.gawali > 0) inclParts.push(`Gawali ${formatCurrency(exp.gawali)}`);
        if (exp.bhussa > 0) inclParts.push(`Bhussa ${formatCurrency(exp.bhussa)}`);

        entries.push({
          date: date,
          description: `Sales (${s.qty} pcs) - Net of ${deductedHeads.join('/')}`,
          subDescription: `incl. ${inclParts.join(' · ')}`,
          debit: 0,
          credit: netAmount,
          type: 'sale'
        });
      });

      // Add cash entries:
      //  - PAYMENT → debit line (we paid the bepaari)
      //  - CASH_ADV → debit line (bepaari took cash advance)
      //  - MOTOR/BHUSSA/GAWALI on a SALE date → already netted above, skip here
      //  - MOTOR/BHUSSA/GAWALI on a NON-sale date → still show as standalone debit
      statement.cash_entries.forEach(c => {
        if (c.sub_type === "PAYMENT") {
          entries.push({
            date: c.date,
            description: `Payment (${c.mode})`,
            debit: c.amount,
            credit: 0,
            type: 'cash'
          });
        } else if (SAME_DAY_DEDUCTIONS.includes(c.sub_type) && saleDates.has(c.date)) {
          // already absorbed into the Sales line for that date
          return;
        } else {
          entries.push({
            date: c.date,
            description: `${c.sub_type}${c.particulars ? ' - ' + c.particulars : ''}`,
            debit: c.amount,
            credit: 0,
            type: 'expense'
          });
        }
      });
      
    } else {
      // DUKANDAR LEDGER: Show purchases, discounts, and receipts
      
      // Add sales/purchase entries
      statement.sales.forEach(s => {
        const purchaseAmt = s.dukandar_amount || s.gross_amount;
        entries.push({
          date: s.date,
          description: `Purchase from ${s.bepaari_name} (${s.quantity} pcs)${s.dukandar_rate ? ` @ ${formatCurrency(s.dukandar_rate)}` : ''}`,
          debit: purchaseAmt,
          credit: 0,
          type: 'sale'
        });
        // If there's a discount on this sale, show it as credit
        if (s.discount > 0) {
          entries.push({
            date: s.date,
            description: `Discount on above`,
            debit: 0,
            credit: s.discount,
            type: 'discount'
          });
        }
      });
      
      // Add receipt/refund entries
      // Receipt amount = full settlement (includes bf_disc portion)
      // bf_disc is NOT a separate credit — it's just tracking how much of the receipt was a discount
      // REFUND = we paid dukandar back his credit; opposite of receipt → goes to debit
      statement.cash_entries.forEach(c => {
        const desc = c.bf_disc > 0
          ? `${c.sub_type} (${c.mode}) [incl. BF Disc ${formatCurrency(c.bf_disc)}]`
          : `${c.sub_type} (${c.mode})${c.particulars ? ' - ' + c.particulars : ''}`;
        const isRefund = c.sub_type === "REFUND";
        entries.push({
          date: c.date,
          description: desc,
          debit: isRefund ? c.amount : 0,
          credit: isRefund ? 0 : c.amount,
          type: 'cash'
        });
      });
    }
    
    // Add adjustments (for both)
    if (statement.adjustments) {
      const expenseHeads = ["MANDI_EXPENSE", "BF_DISCOUNT"];
      statement.adjustments.forEach(a => {
        const isExpenseWriteoff = expenseHeads.includes(a.debit_type) || expenseHeads.includes(a.credit_type);
        
        if (isBepari) {
          if (a.direction === "CREDIT" && isExpenseWriteoff) {
            // Expense write-off CREDIT to Bepaari = increases our payable (extra payment)
            entries.push({
              date: a.date,
              description: `Write-off: ${a.debit_party_name} (${a.narration || 'Expense adjustment'})`,
              debit: 0,
              credit: a.amount,
              type: 'adjustment'
            });
          } else {
            // Use backend-computed directional debit/credit when available (new universal logic).
            // Falls back to legacy direction-based interpretation if backend hasn't provided them.
            const hasNew = a.ledger_debit !== undefined || a.ledger_credit !== undefined;
            entries.push({
              date: a.date,
              description: `JV: ${a.effect}`,
              debit: hasNew ? (a.ledger_debit || 0) : (a.direction === "CREDIT" ? a.amount : 0),
              credit: hasNew ? (a.ledger_credit || 0) : (a.direction === "DEBIT" ? a.amount : 0),
              type: 'adjustment'
            });
          }
        } else {
          if (a.direction === "CREDIT" && isExpenseWriteoff) {
            // Expense write-off CREDIT to Dukandar = reduces receivable
            entries.push({
              date: a.date,
              description: `Write-off: ${a.debit_party_name} (${a.narration || 'Expense adjustment'})`,
              debit: 0,
              credit: a.amount,
              type: 'adjustment'
            });
          } else {
            const hasNew = a.ledger_debit !== undefined || a.ledger_credit !== undefined;
            entries.push({
              date: a.date,
              description: `JV: ${a.effect}`,
              debit: hasNew ? (a.ledger_debit || 0) : (a.direction === "CREDIT" ? a.amount : 0),
              credit: hasNew ? (a.ledger_credit || 0) : (a.direction === "DEBIT" ? a.amount : 0),
              type: 'adjustment'
            });
          }
        }
      });
    }
    
    // Add balance transfers as informational rows (NOT affecting running balance)
    // Balance transfers already modify opening_balance directly in the DB
    if (statement.balance_transfers) {
      statement.balance_transfers.forEach(t => {
        entries.push({
          date: t.date,
          description: `Transfer: ${t.effect}`,
          debit: 0,
          credit: 0,
          type: 'transfer',
          infoOnly: true,
          transferAmount: t.amount,
          transferDirection: t.direction
        });
      });
    }
    
    // Sort by date
    entries.sort((a, b) => a.date.localeCompare(b.date));
    
    // Calculate running balance
    let balance = statement.summary.opening_balance || 0;
    entries.forEach(e => {
      if (isBepari) {
        // Bepaari: Credits increase payable, Debits reduce it
        balance = balance + e.credit - e.debit;
      } else {
        // Dukandar: Debits increase receivable, Credits reduce it
        balance = balance + e.debit - e.credit;
      }
      e.balance = balance;
    });
    
    return entries;
  };

  const ledgerEntries = statement ? buildLedgerEntries() : [];
  const totalDebit = ledgerEntries.reduce((sum, e) => sum + e.debit, 0);
  const totalCredit = ledgerEntries.reduce((sum, e) => sum + e.credit, 0);
  const closingBalance = ledgerEntries.length > 0 ? ledgerEntries[ledgerEntries.length - 1].balance : (statement?.summary?.opening_balance || 0);
  
  // Determine closing balance label
  const getClosingLabel = () => {
    if (!statement) return "";
    const name = statement.party.name;
    if (partyType === "bepaari") {
      return closingBalance >= 0 ? `To Pay ${name}` : `To Receive from ${name}`;
    } else {
      // Dukandar AND Advance Party: positive = receivable
      return closingBalance >= 0 ? `To Receive from ${name}` : `To Pay ${name}`;
    }
  };

  const handlePrint = () => {
    if (!statement) return;
    const printDate = new Date().toISOString().split('T')[0];
    const isBepari = partyType === "bepaari";
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ledger - ${statement.party.name}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
          body { font-family: 'IBM Plex Sans', sans-serif; padding: 20px; font-size: 12px; }
          .header { text-align: center; border-bottom: 3px solid #C5A55A; padding-bottom: 12px; margin-bottom: 16px; }
          .header .firm { color: #C5A55A; font-size: 11px; font-weight: 700; letter-spacing: 2px; }
          .header h1 { font-family: 'Cormorant Garamond', serif; color: #1B2A4A; font-size: 20px; margin: 4px 0; }
          .header .meta { color: #475569; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #1B2A4A; color: white; padding: 8px; font-size: 10px; text-transform: uppercase; text-align: center; }
          td { padding: 6px 8px; border-bottom: 1px solid #E2E0D8; text-align: right; }
          td:first-child, td:nth-child(2) { text-align: left; }
          .total-row { background: rgba(197,165,90,0.1); font-weight: bold; border-top: 2px solid #C5A55A; }
          .opening-row { background: rgba(27,42,74,0.06); font-weight: bold; }
          .closing-row { background: #1B2A4A; color: white; font-weight: bold; font-size: 13px; }
          .footer { text-align: center; margin-top: 16px; font-size: 9px; color: #94a3b8; border-top: 1px dashed #C5A55A; padding-top: 8px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <p class="firm">HAJI MUSHTAQ NANA & SONS</p>
          <h1>PARTY STATEMENT — ${statement.party.name}</h1>
          <p class="meta">${partyType.toUpperCase()} | Phone: ${statement.party.phone || 'N/A'} | Period: ${fmtDate(fromDate) || 'Beginning'} to ${fmtDate(toDate) || fmtDate(printDate)}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:80px">Date</th>
              <th>Description</th>
              <th style="width:100px">${isBepari ? 'Debit (-)' : 'Debit (+)'}</th>
              <th style="width:100px">${isBepari ? 'Credit (+)' : 'Credit (-)'}</th>
              <th style="width:100px">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr class="opening-row">
              <td>-</td>
              <td>Opening Balance</td>
              <td>-</td>
              <td>-</td>
              <td>${(statement.summary.opening_balance || 0).toLocaleString('en-IN')}</td>
            </tr>
            ${ledgerEntries.map(e => `
              <tr${e.infoOnly ? ' style="color:#999;font-style:italic"' : ''}>
                <td>${fmtDate(e.date)}</td>
                <td>${e.description}${e.infoOnly ? ` (${e.transferAmount.toLocaleString('en-IN')} — already in opening bal.)` : ''}${e.subDescription ? `<div style="font-size:10px;color:#94a3b8;font-style:italic;margin-top:2px">${e.subDescription}</div>` : ''}</td>
                <td>${e.debit > 0 ? e.debit.toLocaleString('en-IN') : '-'}</td>
                <td>${e.credit > 0 ? e.credit.toLocaleString('en-IN') : '-'}</td>
                <td>${e.balance.toLocaleString('en-IN')}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="2">TOTAL</td>
              <td>${totalDebit.toLocaleString('en-IN')}</td>
              <td>${totalCredit.toLocaleString('en-IN')}</td>
              <td>-</td>
            </tr>
            <tr class="closing-row">
              <td colspan="4">${getClosingLabel()}</td>
              <td>${Math.abs(closingBalance).toLocaleString('en-IN')}</td>
            </tr>
          </tbody>
        </table>
        <p class="footer">Generated from Mandi Accounting App | Haji Mushtaq Nana & Sons</p>
      </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const parties = partyType === "bepaari" ? bepaaris : partyType === "dukandar" ? dukandars : advanceParties;

  return (
    <div className="page">
      <h2>Party Statement / Ledger</h2>
      <p className="hint">Get complete transaction history with running balance. All entries in date sequence.</p>
      
      <div className="filter-bar">
        <select value={partyType} onChange={(e) => { setPartyType(e.target.value); setPartyId(""); setStatement(null); }}>
          <option value="bepaari">Bepaari</option>
          <option value="dukandar">Dukandar</option>
          <option value="advance">Advance Party</option>
        </select>
        <SearchableSelect
          options={parties.map(p => ({ value: p.id, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
          value={partyId}
          onChange={(val) => setPartyId(val)}
          placeholder={`Select ${partyType}`}
          testId="ps-party-select"
        />
        <label>From:</label><input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <label>To:</label><input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        <button className="btn-primary" onClick={fetchStatement}>Get Statement</button>
        {statement && <button className="btn-print" onClick={handlePrint}>🖨️ Print / Save PDF</button>}
        {statement && <button className="btn-excel" onClick={downloadExcel}>Download CSV</button>}
      </div>

      {loading && <div className="loading">Loading...</div>}

      {statement && (
        <div className="statement-container">
          <div className="party-info">
            <h3>{statement.party.name}</h3>
            <p>Phone: {statement.party.phone || "N/A"} | Period: {fromDate || "Beginning"} to {toDate || "Current"}</p>
            {partyType === "bepaari" && (() => {
              const b = bepaaris.find(x => x.id === partyId) || {};
              const flat = b.flat_rate_per_goat;
              const commLabel = flat
                ? `Commission: ₹${flat}/goat (flat)`
                : `Commission: ${b.commission_percent != null ? b.commission_percent : (settings.commission_rate ?? 4)}%${b.commission_percent != null ? ' (override)' : ''}`;
              return (
                <p className="rate-snapshot" data-testid="rate-snapshot">
                  <span className="rate-snapshot-label">Rates applied:</span> {commLabel} · JB: ₹{settings.jb_rate ?? 10}/pc · KK: ₹{settings.kk_fixed ?? 100}/day
                </p>
              );
            })()}
          </div>

          <div className="ledger-table desktop-only">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>{partyType === "bepaari" ? "Debit (-)" : "Debit (+)"}</th>
                  <th>{partyType === "bepaari" ? "Credit (+)" : "Credit (-)"}</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="opening-row">
                  <td>-</td>
                  <td><strong>Opening Balance</strong></td>
                  <td>-</td>
                  <td>-</td>
                  <td><strong>{formatCurrency(statement.summary.opening_balance || 0)}</strong></td>
                </tr>
                {ledgerEntries.map((e, i) => (
                  <tr key={i} className={e.type === 'sale' ? 'sale-row' : e.type === 'adjustment' ? 'adjustment-row' : e.type === 'transfer' ? 'transfer-info-row' : ''}>
                    <td>{fmtDate(e.date)}</td>
                    <td>
                      {e.description}{e.infoOnly && <span className="transfer-note"> ({formatCurrency(e.transferAmount)} — already in opening bal.)</span>}
                      {e.subDescription && <div style={{fontSize: '11px', color: '#94a3b8', marginTop: '2px', fontStyle: 'italic'}}>{e.subDescription}</div>}
                    </td>
                    <td>{e.debit > 0 ? formatCurrency(e.debit) : "-"}</td>
                    <td>{e.credit > 0 ? formatCurrency(e.credit) : "-"}</td>
                    <td className={e.balance >= 0 ? "positive" : "negative"}>{formatCurrency(e.balance)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="2"><strong>TOTAL</strong></td>
                  <td><strong>{formatCurrency(totalDebit)}</strong></td>
                  <td><strong>{formatCurrency(totalCredit)}</strong></td>
                  <td>-</td>
                </tr>
                <tr className="closing-row">
                  <td colSpan="4"><strong>{getClosingLabel()}</strong></td>
                  <td><strong>{formatCurrency(Math.abs(closingBalance))}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile statement cards */}
          <div className="statement-mobile mobile-only">
            <div className="stmt-mobile-row stmt-opening">
              <div className="stmt-mobile-desc"><strong>Opening Balance</strong></div>
              <div className="stmt-mobile-bal"><strong>{formatCurrency(statement.summary.opening_balance || 0)}</strong></div>
            </div>
            {ledgerEntries.map((e, i) => (
              <div key={i} className={`stmt-mobile-row ${e.type === 'sale' ? 'stmt-sale' : e.type === 'adjustment' ? 'stmt-adj' : e.type === 'transfer' ? 'stmt-transfer' : ''}`}>
                <div className="stmt-mobile-top">
                  <span className="stmt-mobile-date">{fmtDate(e.date)}</span>
                  <span className={`stmt-mobile-bal ${e.balance >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(e.balance)}</span>
                </div>
                <div className="stmt-mobile-desc">
                  {e.description}{e.infoOnly && <span className="transfer-note"> ({formatCurrency(e.transferAmount)} — in opening)</span>}
                  {e.subDescription && <div style={{fontSize: '10.5px', color: '#94a3b8', marginTop: '2px', fontStyle: 'italic', lineHeight: 1.4}}>{e.subDescription}</div>}
                </div>
                <div className="stmt-mobile-amounts">
                  {e.debit > 0 && <span className="stmt-debit">{partyType === "bepaari" ? "Dr (-)" : "Dr (+)"} {formatCurrency(e.debit)}</span>}
                  {e.credit > 0 && <span className="stmt-credit">{partyType === "bepaari" ? "Cr (+)" : "Cr (-)"} {formatCurrency(e.credit)}</span>}
                </div>
              </div>
            ))}
            <div className="stmt-mobile-row stmt-total">
              <div className="stmt-mobile-top">
                <span><strong>TOTAL</strong></span>
              </div>
              <div className="stmt-mobile-amounts">
                <span className="stmt-debit">Dr {formatCurrency(totalDebit)}</span>
                <span className="stmt-credit">Cr {formatCurrency(totalCredit)}</span>
              </div>
            </div>
            <div className="stmt-mobile-row stmt-closing">
              <div className="stmt-mobile-top">
                <span><strong>{getClosingLabel()}</strong></span>
                <span className="stmt-mobile-bal positive"><strong>{formatCurrency(Math.abs(closingBalance))}</strong></span>
              </div>
            </div>
          </div>

          {/* Payment Aging (FIFO) — Dukandars only */}
          {agingData && agingData.length > 0 && partyType === "dukandar" && (
            <div className="aging-section" data-testid="payment-aging">
              <h3 className="aging-title">Payment Aging (FIFO)</h3>
              <p className="hint">Payments applied against oldest purchases first</p>
              <table className="aging-table desktop-only">
                <thead>
                  <tr>
                    <th>Purchase Date</th>
                    <th>Purchase Amount</th>
                    <th>Cleared By Payments</th>
                    <th>Remaining Unpaid</th>
                    <th>Days Old</th>
                    <th>Overdue?</th>
                  </tr>
                </thead>
                <tbody>
                  {agingData.map((t, i) => (
                    <tr key={i} className={t.status === 'overdue' ? 'aging-row-overdue' : t.status === 'cleared' ? 'aging-row-cleared' : ''}>
                      <td>{t.date === 'Opening' ? 'B/F Opening' : fmtDate(t.date)}</td>
                      <td>{formatCurrency(t.original)}</td>
                      <td className={t.remaining === 0 ? 'aging-full' : t.cleared > 0 ? 'aging-partial' : ''}>
                        {formatCurrency(t.cleared)}{t.remaining === 0 ? ' ✅' : t.cleared > 0 ? ' partial' : ''}
                      </td>
                      <td className={t.remaining > 0 ? 'aging-unpaid' : ''}>{formatCurrency(t.remaining)}</td>
                      <td>{t.days_old === 999 ? 'B/F' : `${t.days_old}d`}</td>
                      <td className={`aging-status-${t.status}`}>
                        {t.status === 'cleared' ? 'No — Cleared' : t.status === 'overdue' ? 'YES' : 'Within limit'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile aging cards */}
              <div className="aging-mobile mobile-only">
                {agingData.map((t, i) => (
                  <div key={i} className={`aging-mobile-card ${t.status === 'overdue' ? 'aging-row-overdue' : t.status === 'cleared' ? 'aging-row-cleared' : ''}`}>
                    <div className="amc-top">
                      <span><strong>{t.date === 'Opening' ? 'B/F Opening' : fmtDate(t.date)}</strong></span>
                      <span className="amc-days">{t.days_old === 999 ? 'B/F' : `${t.days_old}d`}{t.status === 'overdue' ? ' OVERDUE' : ''}</span>
                    </div>
                    <div className="lmc-row"><span>Purchase</span><span>{formatCurrency(t.original)}</span></div>
                    <div className="lmc-row"><span>Cleared</span><span className={t.remaining === 0 ? 'aging-full' : 'aging-partial'}>{formatCurrency(t.cleared)}{t.remaining === 0 ? ' ✅' : t.cleared > 0 ? ' (partial)' : ''}</span></div>
                    <div className="lmc-row"><span>Remaining</span><span className={t.remaining > 0 ? 'aging-unpaid' : ''}>{formatCurrency(t.remaining)}</span></div>
                  </div>
                ))}
              </div>
              <div className="aging-footer">
                <span>Overdue: <strong className="aging-unpaid">{formatCurrency(agingData.filter(t => t.status === 'overdue').reduce((s, t) => s + t.remaining, 0))}</strong></span>
                <span>Within limit: <strong>{formatCurrency(agingData.filter(t => t.status === 'within_limit').reduce((s, t) => s + t.remaining, 0))}</strong></span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== BEPAARI LEDGER ==============
const BepariLedger = () => {
  const [ledger, setLedger] = useState([]);
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState("name-asc");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    const url = asOnDate ? `${API}/bepaari-ledger?as_on_date=${asOnDate}` : `${API}/bepaari-ledger`;
    const res = await axios.get(url);
    setLedger(res.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [asOnDate]);

  if (loading) return <div className="loading">Loading...</div>;

  // Sorting
  const sortedLedger = [...ledger].filter(b => b.balance !== 0).sort((a, b) => {
    if (sortBy === "name-asc") return a.name.localeCompare(b.name);
    if (sortBy === "name-desc") return b.name.localeCompare(a.name);
    if (sortBy === "balance-desc") return b.balance - a.balance;
    if (sortBy === "balance-asc") return a.balance - b.balance;
    return 0;
  });

  const totals = ledger.reduce((acc, b) => ({
    gross: acc.gross + b.gross_sales, qty: acc.qty + b.quantity, comm: acc.comm + b.commission,
    kk: acc.kk + b.kk, jb: acc.jb + b.jb, ded: acc.ded + b.total_deductions, 
    pay: acc.pay + b.payments, adj: acc.adj + (b.adjustments || 0), bal: acc.bal + b.balance
  }), { gross: 0, qty: 0, comm: 0, kk: 0, jb: 0, ded: 0, pay: 0, adj: 0, bal: 0 });

  const handlePrint = () => {
    const printDate = new Date().toLocaleDateString('en-IN');
    const printContent = `
      <html><head><title>Bepaari Ledger</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        body { font-family: 'IBM Plex Sans', sans-serif; padding: 20px; font-size: 12px; }
        .header { text-align: center; border-bottom: 3px solid #C5A55A; padding-bottom: 12px; margin-bottom: 16px; }
        .header .firm { color: #C5A55A; font-size: 11px; font-weight: 700; letter-spacing: 2px; }
        .header h1 { font-family: 'Cormorant Garamond', serif; color: #1B2A4A; font-size: 22px; margin: 4px 0; }
        .header .meta { color: #475569; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #1B2A4A; color: white; padding: 8px 6px; text-align: right; font-size: 10px; text-transform: uppercase; }
        th:first-child { text-align: left; }
        td { padding: 6px; border-bottom: 1px solid #E2E0D8; text-align: right; font-size: 11px; }
        td:first-child { text-align: left; font-weight: 600; }
        .total-row td { border-top: 3px solid #C5A55A; font-weight: 700; background: rgba(197,165,90,0.1); }
        .positive { color: #166534; }
        .negative { color: #991B1B; }
        .footer { text-align: center; margin-top: 16px; font-size: 9px; color: #94a3b8; border-top: 1px dashed #C5A55A; padding-top: 8px; }
      </style></head><body>
        <div class="header"><p class="firm">HAJI MUSHTAQ NANA & SONS</p><h1>BEPAARI LEDGER</h1><p class="meta">${asOnDate ? 'As on: ' + fmtDate(asOnDate) : 'Current'} | Generated: ${printDate}</p></div>
        <table>
          <thead><tr><th>Name</th><th>Opening</th><th>Sales</th><th>Qty</th><th>Comm</th><th>KK</th><th>JB</th><th>Deductions</th><th>Payments</th><th>Adj</th><th>Balance</th></tr></thead>
          <tbody>
            ${sortedLedger.map(b => `
              <tr>
                <td>${b.name}</td><td>${b.opening.toLocaleString('en-IN')}</td><td>${b.gross_sales.toLocaleString('en-IN')}</td>
                <td>${b.quantity}</td><td>${b.commission.toLocaleString('en-IN')}</td><td>${b.kk.toLocaleString('en-IN')}</td><td>${b.jb.toLocaleString('en-IN')}</td>
                <td>${b.total_deductions.toLocaleString('en-IN')}</td><td>${b.payments.toLocaleString('en-IN')}</td>
                <td>${b.adjustments > 0 ? b.adjustments.toLocaleString('en-IN') : '-'}</td>
                <td class="${b.balance >= 0 ? 'positive' : 'negative'}">${b.balance.toLocaleString('en-IN')}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td>TOTAL</td><td>-</td><td>${totals.gross.toLocaleString('en-IN')}</td><td>${totals.qty}</td>
              <td>${totals.comm.toLocaleString('en-IN')}</td><td>${totals.kk.toLocaleString('en-IN')}</td><td>${totals.jb.toLocaleString('en-IN')}</td>
              <td>${totals.ded.toLocaleString('en-IN')}</td><td>${totals.pay.toLocaleString('en-IN')}</td>
              <td>${totals.adj > 0 ? totals.adj.toLocaleString('en-IN') : '-'}</td><td>${totals.bal.toLocaleString('en-IN')}</td>
            </tr>
          </tbody>
        </table>
        <p class="footer">Generated from Mandi Accounting App | Haji Mushtaq Nana & Sons on ${printDate}</p>
      </body></html>`;
    const w = window.open('', '', 'width=1000,height=700');
    w.document.write(printContent);
    w.document.close();
    w.print();
  };

  return (
    <div className="page">
      <h2>Bepaari Ledger</h2>
      <div className="filter-bar">
        <label>As On Date:</label>
        <input type="date" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} />
        {asOnDate && <button className="btn-clear" onClick={() => setAsOnDate("")}>Show Current</button>}
        <button className="btn-print" onClick={handlePrint} data-testid="bepaari-print-btn">Print / Save PDF</button>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
          <option value="balance-desc">Balance (High-Low)</option>
          <option value="balance-asc">Balance (Low-High)</option>
        </select>
      </div>
      <div className="table-container desktop-only">
        <table>
          <thead><tr>
            <th className="sortable" onClick={() => setSortBy(sortBy === "name-asc" ? "name-desc" : "name-asc")}>Name {sortBy.startsWith("name") ? (sortBy === "name-asc" ? "↑" : "↓") : ""}</th>
            <th>Phone</th><th>Opening</th><th>Sales</th><th>Qty</th><th>Comm</th><th>KK</th><th>JB</th><th>Deductions</th><th>Payments</th><th>Adj</th>
            <th className="sortable" onClick={() => setSortBy(sortBy === "balance-desc" ? "balance-asc" : "balance-desc")}>Balance {sortBy.startsWith("balance") ? (sortBy === "balance-desc" ? "↓" : "↑") : ""}</th>
          </tr></thead>
          <tbody>
            {sortedLedger.map((b) => (
              <tr key={b.id} className="clickable-row" onClick={() => navigate(`/party-statement?type=bepaari&id=${b.id}`)}>
                <td><strong>{b.name}</strong></td><td>{b.phone || "-"}</td><td>{formatCurrency(b.opening)}</td><td>{formatCurrency(b.gross_sales)}</td>
                <td>{b.quantity}</td><td>{formatCurrency(b.commission)}</td><td>{formatCurrency(b.kk)}</td><td>{formatCurrency(b.jb)}</td>
                <td>{formatCurrency(b.total_deductions)}</td><td>{formatCurrency(b.payments)}</td>
                <td className="adjustment-col">{b.adjustments > 0 ? formatCurrency(b.adjustments) : "-"}</td>
                <td className={b.balance >= 0 ? "positive" : "negative"}>{formatCurrency(b.balance)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td><strong>TOTAL</strong></td><td></td><td>-</td><td><strong>{formatCurrency(totals.gross)}</strong></td><td><strong>{totals.qty}</strong></td>
              <td><strong>{formatCurrency(totals.comm)}</strong></td><td><strong>{formatCurrency(totals.kk)}</strong></td><td><strong>{formatCurrency(totals.jb)}</strong></td>
              <td><strong>{formatCurrency(totals.ded)}</strong></td><td><strong>{formatCurrency(totals.pay)}</strong></td>
              <td><strong>{totals.adj > 0 ? formatCurrency(totals.adj) : "-"}</strong></td><td><strong>{formatCurrency(totals.bal)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards mobile-only">
        {sortedLedger.map((b) => (
          <div key={b.id} className="ledger-mobile-card" onClick={() => navigate(`/party-statement?type=bepaari&id=${b.id}`)} data-testid="bep-ledger-card">
            <div className="lmc-header">
              <div className="lmc-name">{b.name}</div>
              <div className={`lmc-balance ${b.balance >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(b.balance)}</div>
            </div>
            <div className="lmc-body">
              <div className="lmc-row"><span>Opening</span><span>{formatCurrency(b.opening)}</span></div>
              <div className="lmc-row"><span>Sales ({b.quantity} pcs)</span><span>{formatCurrency(b.gross_sales)}</span></div>
              <div className="lmc-row"><span>Deductions</span><span>-{formatCurrency(b.total_deductions)}</span></div>
              <div className="lmc-row"><span>Payments</span><span>-{formatCurrency(b.payments)}</span></div>
              {b.adjustments > 0 && <div className="lmc-row"><span>Adjustments</span><span>-{formatCurrency(b.adjustments)}</span></div>}
            </div>
          </div>
        ))}
        <div className="ledger-mobile-card lmc-total">
          <div className="lmc-header">
            <div className="lmc-name">TOTAL</div>
            <div className={`lmc-balance ${totals.bal >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(totals.bal)}</div>
          </div>
          <div className="lmc-body">
            <div className="lmc-row"><span>Sales ({totals.qty} pcs)</span><span>{formatCurrency(totals.gross)}</span></div>
            <div className="lmc-row"><span>Deductions</span><span>-{formatCurrency(totals.ded)}</span></div>
            <div className="lmc-row"><span>Payments</span><span>-{formatCurrency(totals.pay)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== DUKANDAR LEDGER ==============
const DukandarLedger = () => {
  const [ledger, setLedger] = useState([]);
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState("name-asc");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    const url = asOnDate ? `${API}/dukandar-ledger?as_on_date=${asOnDate}` : `${API}/dukandar-ledger`;
    const res = await axios.get(url);
    setLedger(res.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [asOnDate]);

  const handlePrint = () => {
    const printDate = new Date().toLocaleDateString('en-IN');
    const printContent = `
      <html><head><title>Patti - Dukandar Outstanding</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        body { font-family: 'IBM Plex Sans', sans-serif; padding: 20px; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .header { text-align: center; border-bottom: 3px solid #C5A55A; padding-bottom: 12px; margin-bottom: 16px; }
        .header .firm { color: #C5A55A; font-size: 11px; font-weight: 700; letter-spacing: 2px; }
        .header h1 { font-family: 'Cormorant Garamond', serif; color: #1B2A4A; font-size: 22px; margin: 4px 0; }
        .header .meta { color: #475569; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #1B2A4A; color: white; padding: 8px 6px; text-align: right; font-size: 10px; text-transform: uppercase; }
        th:first-child { text-align: left; }
        td { padding: 6px; border-bottom: 1px solid #E2E0D8; text-align: right; font-size: 11px; }
        td:first-child { text-align: left; font-weight: 600; }
        .total-row td { border-top: 3px solid #C5A55A; font-weight: 700; background: rgba(197,165,90,0.1); }
        .positive { color: #166534; }
        .negative { color: #991B1B; }
        .aging-red { background: #FEE2E2; }
        .aging-red td:first-child::after { content: " ●"; color: #991B1B; font-weight: 700; }
        .aging-yellow { background: #FEF3C7; }
        .aging-yellow td:first-child::after { content: " ●"; color: #92400E; font-weight: 700; }
        .aging-badge { display: inline-block; margin-left: 6px; padding: 1px 6px; font-size: 9px; font-weight: 700; border-radius: 3px; }
        .badge-red { background: #991B1B; color: #fff; }
        .badge-yellow { background: #F59E0B; color: #1B2A4A; }
        .legend { margin-bottom: 10px; font-size: 10px; color: #475569; display: flex; gap: 14px; }
        .legend .dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }
        .legend .dot.red { background: #991B1B; }
        .legend .dot.yellow { background: #F59E0B; }
        .legend .dot.green { background: #166534; }
        .footer { text-align: center; margin-top: 16px; font-size: 9px; color: #94a3b8; border-top: 1px dashed #C5A55A; padding-top: 8px; }
      </style></head><body>
        <div class="header"><p class="firm">HAJI MUSHTAQ NANA & SONS</p><h1>PATTI &mdash; DUKANDAR OUTSTANDING</h1><p class="meta">${asOnDate ? 'As on: ' + fmtDate(asOnDate) : 'Current'} | Generated: ${printDate}</p></div>
        <div class="legend">
          <span><span class="dot green"></span>0-7 days</span>
          <span><span class="dot yellow"></span>8-15 days (Caution)</span>
          <span><span class="dot red"></span>15+ days (Overdue)</span>
        </div>
        <table>
          <thead><tr><th>Name</th><th>Opening</th><th>Purchases</th><th>Discounts</th><th>Receipts</th><th>BF Disc</th><th>Adj</th><th>Balance</th></tr></thead>
          <tbody>
            ${sortedLedger.map(d => {
              const days = d.oldest_unpaid_days;
              let rowClass = '';
              let badge = '';
              if (d.balance > 0 && days != null) {
                if (days >= 999) { rowClass = 'aging-red'; badge = '<span class="aging-badge badge-red">B/F OVERDUE</span>'; }
                else if (days > 15) { rowClass = 'aging-red'; badge = `<span class="aging-badge badge-red">${days}D OVERDUE</span>`; }
                else if (days > 7) { rowClass = 'aging-yellow'; badge = `<span class="aging-badge badge-yellow">${days}D</span>`; }
              }
              return `
              <tr class="${rowClass}">
                <td>${d.name}${badge}</td>
                <td>${d.opening.toLocaleString('en-IN')}</td>
                <td>${d.purchases.toLocaleString('en-IN')}</td>
                <td>${d.discounts.toLocaleString('en-IN')}</td>
                <td>${d.receipts.toLocaleString('en-IN')}</td>
                <td>${d.bf_disc > 0 ? d.bf_disc.toLocaleString('en-IN') : '-'}</td>
                <td>${((d.adjustments || 0) + (d.writeoffs || 0)) > 0 ? ((d.adjustments || 0) + (d.writeoffs || 0)).toLocaleString('en-IN') : '-'}</td>
                <td class="${d.balance >= 0 ? 'positive' : 'negative'}">${d.balance.toLocaleString('en-IN')}</td>
              </tr>`;
            }).join('')}
            <tr class="total-row">
              <td>TOTAL PATTI</td>
              <td>-</td>
              <td>${totals.purchases.toLocaleString('en-IN')}</td>
              <td>${totals.discounts.toLocaleString('en-IN')}</td>
              <td>${totals.receipts.toLocaleString('en-IN')}</td>
              <td>${totals.bf_disc > 0 ? totals.bf_disc.toLocaleString('en-IN') : '-'}</td>
              <td>${totals.adj > 0 ? totals.adj.toLocaleString('en-IN') : '-'}</td>
              <td>${totals.balance.toLocaleString('en-IN')}</td>
            </tr>
          </tbody>
        </table>
        <p class="footer">Generated from Mandi Accounting App | Haji Mushtaq Nana & Sons on ${printDate}</p>
      </body></html>`;
    const w = window.open('', '', 'width=1000,height=700');
    w.document.write(printContent);
    w.document.close();
    w.print();
  };

  if (loading) return <div className="loading">Loading...</div>;

  // Aging helper — uses FIFO-based oldest_unpaid_days from backend (matches Party Statement aging)
  const getAgingClass = (d) => {
    if (d.balance <= 0 || d.oldest_unpaid_days == null) return '';
    const days = d.oldest_unpaid_days;
    if (days > 15) return 'aging-red';
    if (days > 7) return 'aging-yellow';
    return '';
  };
  const getAgingLabel = (d) => {
    if (d.balance <= 0 || d.oldest_unpaid_days == null) return '';
    const days = d.oldest_unpaid_days;
    if (days >= 999) return 'B/F overdue';
    if (days > 15) return `${days}d overdue`;
    if (days > 7) return `${days}d`;
    return '';
  };

  // Sorting
  const sortedLedger = [...ledger].filter(d => d.balance !== 0).sort((a, b) => {
    if (sortBy === "name-asc") return a.name.localeCompare(b.name);
    if (sortBy === "name-desc") return b.name.localeCompare(a.name);
    if (sortBy === "balance-desc") return b.balance - a.balance;
    if (sortBy === "balance-asc") return a.balance - b.balance;
    return 0;
  });

  const totals = ledger.reduce((acc, d) => ({
    purchases: acc.purchases + d.purchases, discounts: acc.discounts + d.discounts, 
    receipts: acc.receipts + d.receipts, bf_disc: acc.bf_disc + (d.bf_disc || 0), adj: acc.adj + (d.adjustments || 0) + (d.writeoffs || 0), balance: acc.balance + d.balance
  }), { purchases: 0, discounts: 0, receipts: 0, bf_disc: 0, adj: 0, balance: 0 });

  return (
    <div className="page">
      <h2>Dukandar Ledger (Patti)</h2>
      <div className="filter-bar">
        <label>As On Date:</label>
        <input type="date" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} />
        {asOnDate && <button className="btn-clear" onClick={() => setAsOnDate("")}>Show Current</button>}
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
          <option value="balance-desc">Balance (High-Low)</option>
          <option value="balance-asc">Balance (Low-High)</option>
        </select>
        <button className="btn-print" onClick={handlePrint}>Print / Save PDF</button>
      </div>
      <div className="aging-legend">
        <span className="aging-legend-item"><span className="aging-dot normal"></span> 0-7 days</span>
        <span className="aging-legend-item"><span className="aging-dot yellow"></span> 8-15 days (Caution)</span>
        <span className="aging-legend-item"><span className="aging-dot red"></span> 15+ days (Overdue)</span>
      </div>
      <div className="table-container desktop-only">
        <table>
          <thead><tr>
            <th className="sortable" onClick={() => setSortBy(sortBy === "name-asc" ? "name-desc" : "name-asc")}>Name {sortBy.startsWith("name") ? (sortBy === "name-asc" ? "↑" : "↓") : ""}</th>
            <th>Phone</th><th>Opening</th><th>Purchases</th><th>Discounts</th><th>Net Recv</th><th>Receipts</th><th>BF Disc</th><th>Adj</th>
            <th className="sortable" onClick={() => setSortBy(sortBy === "balance-desc" ? "balance-asc" : "balance-desc")}>Balance {sortBy.startsWith("balance") ? (sortBy === "balance-desc" ? "↓" : "↑") : ""}</th>
          </tr></thead>
          <tbody>
            {sortedLedger.map((d) => (
              <tr key={d.id} className={`clickable-row ${getAgingClass(d)}`} onClick={() => navigate(`/party-statement?type=dukandar&id=${d.id}`)}>
                <td><strong>{d.name}</strong>{getAgingLabel(d) && <span className="aging-badge">{getAgingLabel(d)}</span>}</td><td>{d.phone || "-"}</td><td>{formatCurrency(d.opening)}</td><td>{formatCurrency(d.purchases)}</td>
                <td>{formatCurrency(d.discounts)}</td><td>{formatCurrency(d.net_receivable)}</td><td>{formatCurrency(d.receipts)}</td>
                <td className="bf-disc-col">{d.bf_disc > 0 ? formatCurrency(d.bf_disc) : "-"}</td>
                <td className="adjustment-col">{((d.adjustments || 0) + (d.writeoffs || 0)) > 0 ? formatCurrency((d.adjustments || 0) + (d.writeoffs || 0)) : "-"}</td>
                <td className={d.balance >= 0 ? "positive" : "negative"}>{formatCurrency(d.balance)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td><strong>TOTAL</strong></td><td></td><td>-</td><td><strong>{formatCurrency(totals.purchases)}</strong></td><td><strong>{formatCurrency(totals.discounts)}</strong></td>
              <td>-</td><td><strong>{formatCurrency(totals.receipts)}</strong></td>
              <td><strong>{totals.bf_disc > 0 ? formatCurrency(totals.bf_disc) : "-"}</strong></td>
              <td><strong>{totals.adj > 0 ? formatCurrency(totals.adj) : "-"}</strong></td><td><strong>{formatCurrency(totals.balance)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards mobile-only">
        {sortedLedger.map((d) => (
          <div key={d.id} className={`ledger-mobile-card ${getAgingClass(d)}`} onClick={() => navigate(`/party-statement?type=dukandar&id=${d.id}`)} data-testid="duk-ledger-card">
            <div className="lmc-header">
              <div className="lmc-name">
                {d.name}
                {getAgingLabel(d) && <span className="aging-badge">{getAgingLabel(d)}</span>}
              </div>
              <div className={`lmc-balance ${d.balance >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(d.balance)}</div>
            </div>
            <div className="lmc-body">
              <div className="lmc-row"><span>Opening</span><span>{formatCurrency(d.opening)}</span></div>
              <div className="lmc-row"><span>Purchases</span><span>{formatCurrency(d.purchases)}</span></div>
              {d.discounts > 0 && <div className="lmc-row"><span>Discounts</span><span>-{formatCurrency(d.discounts)}</span></div>}
              <div className="lmc-row"><span>Receipts</span><span>-{formatCurrency(d.receipts)}</span></div>
              {d.bf_disc > 0 && <div className="lmc-row"><span>BF Disc</span><span>-{formatCurrency(d.bf_disc)}</span></div>}
              {((d.adjustments || 0) + (d.writeoffs || 0)) > 0 && <div className="lmc-row"><span>Adjustments</span><span>-{formatCurrency((d.adjustments || 0) + (d.writeoffs || 0))}</span></div>}
            </div>
          </div>
        ))}
        <div className="ledger-mobile-card lmc-total">
          <div className="lmc-header">
            <div className="lmc-name">TOTAL</div>
            <div className={`lmc-balance ${totals.balance >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(totals.balance)}</div>
          </div>
          <div className="lmc-body">
            <div className="lmc-row"><span>Purchases</span><span>{formatCurrency(totals.purchases)}</span></div>
            <div className="lmc-row"><span>Receipts</span><span>-{formatCurrency(totals.receipts)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== BALANCE SHEET ==============
const BalanceSheet = () => {
  const [data, setData] = useState(null);
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    const url = asOnDate ? `${API}/balance-sheet?as_on_date=${asOnDate}` : `${API}/balance-sheet`;
    const res = await axios.get(url);
    setData(res.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [asOnDate]);

  if (loading) return <div className="loading">Loading...</div>;

  const { liabilities: L, assets: A, difference } = data;

  // Clickable head row → head-statement page (with as_on_date as to_date)
  const goHead = (head) => {
    const q = asOnDate ? `?to_date=${asOnDate}` : '';
    navigate(`/head-statement/${head}${q}`);
  };
  const headLinkStyle = { cursor: 'pointer', color: '#1e40af', textDecoration: 'underline dotted', fontWeight: 600 };
  const HeadLink = ({ head, children, testid }) => (
    <span data-testid={testid} onClick={() => goHead(head)} style={headLinkStyle} title={`View ${head} statement`}>{children}</span>
  );

  return (
    <div className="page">
      <h2>Balance Sheet {data.as_on_date !== "Current" ? `(As on ${data.as_on_date})` : ""}</h2>
      
      <div className="filter-bar">
        <label>As On Date:</label>
        <input type="date" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} />
        {asOnDate && <button className="btn-clear" onClick={() => setAsOnDate("")}>Show Current</button>}
      </div>

      <div className={`balance-status ${difference === 0 ? "tallied" : "not-tallied"}`}>
        {difference === 0 ? "BALANCE SHEET TALLIED" : `DIFFERENCE: ${formatCurrency(difference)}`}
      </div>

      <div style={{fontSize:'12px', color:'#718096', margin:'6px 0 12px', textAlign:'center'}}>
        Tip: click any underlined head or party name to open its full statement.
      </div>
      
      <div className="bs-container">
        <div className="bs-section">
          <h3>LIABILITIES</h3>
          <table>
            <tbody>
              {/* Capital - Individual names */}
              {L.capital_list && L.capital_list.length > 0 ? (
                <>
                  <tr className="section-header"><td colSpan="2"><strong><HeadLink head="CAPITAL" testid="bs-capital-head">Capital</HeadLink></strong></td></tr>
                  {L.capital_list.map((p, i) => (
                    <tr key={i} className="sub-item"><td>&nbsp;&nbsp;{p.name}</td><td>{formatCurrency(p.amount)}</td></tr>
                  ))}
                  {L.capital_list.length > 1 && <tr className="sub-total"><td>&nbsp;&nbsp;<em>Total Capital</em></td><td><em>{formatCurrency(L.capital)}</em></td></tr>}
                </>
              ) : (
                <tr><td><HeadLink head="CAPITAL" testid="bs-capital-row">Capital</HeadLink></td><td>{formatCurrency(L.capital)}</td></tr>
              )}
              
              {/* Loans - Individual names */}
              {L.loans_list && L.loans_list.length > 0 ? (
                <>
                  <tr className="section-header"><td colSpan="2"><strong>Loans</strong></td></tr>
                  {L.loans_list.map((p, i) => (
                    <tr key={i} className="sub-item"><td>&nbsp;&nbsp;{p.name}</td><td>{formatCurrency(p.amount)}</td></tr>
                  ))}
                  {L.loans_list.length > 1 && <tr className="sub-total"><td>&nbsp;&nbsp;<em>Total Loans</em></td><td><em>{formatCurrency(L.loans)}</em></td></tr>}
                </>
              ) : L.loans > 0 ? (
                <tr><td>Loans</td><td>{formatCurrency(L.loans)}</td></tr>
              ) : null}
              
              {/* Amanat - Individual names */}
              {L.amanat_list && L.amanat_list.length > 0 ? (
                <>
                  <tr className="section-header"><td colSpan="2"><strong><HeadLink head="AMANAT" testid="bs-amanat-head">Amanat</HeadLink></strong></td></tr>
                  {L.amanat_list.map((p, i) => (
                    <tr key={i} className="sub-item"><td>&nbsp;&nbsp;{p.name}</td><td>{formatCurrency(p.amount)}</td></tr>
                  ))}
                  {L.amanat_list.length > 1 && <tr className="sub-total"><td>&nbsp;&nbsp;<em>Total Amanat</em></td><td><em>{formatCurrency(L.amanat)}</em></td></tr>}
                </>
              ) : L.amanat > 0 ? (
                <tr><td><HeadLink head="AMANAT" testid="bs-amanat-row">Amanat</HeadLink></td><td>{formatCurrency(L.amanat)}</td></tr>
              ) : null}
              
              <tr><td><span data-testid="bs-bepaari-payables" onClick={() => navigate('/bepaari-ledger')} style={headLinkStyle} title="Go to Bepaari Ledger">Bepaari Payables</span></td><td>{formatCurrency(L.bepaari_payables)}</td></tr>
              {L.dukandar_advances > 0 && <tr><td><span onClick={() => navigate('/dukandar-ledger')} style={headLinkStyle} title="Go to Dukandar Ledger">Dukandar Advances</span></td><td>{formatCurrency(L.dukandar_advances)}</td></tr>}
              <tr className="sub-item"><td><HeadLink head="JB" testid="bs-jb-row">JB Total</HeadLink></td><td>{formatCurrency(L.jb.total)}</td></tr>
              <tr className="sub-item"><td><HeadLink head="KK" testid="bs-kk-row">KK Total</HeadLink></td><td>{formatCurrency(L.kk.total)}</td></tr>
              <tr className="sub-item"><td><HeadLink head="COMMISSION" testid="bs-commission-row">Commission Total</HeadLink></td><td>{formatCurrency(L.commission.total)}</td></tr>
              <tr className="sub-item" style={{fontSize:'11px',color:'#718096'}}><td>&nbsp;&nbsp;Gross Earned: {formatCurrency(L.commission.earned)}{L.commission.rate_diff > 0 ? ` + Rate Diff: ${formatCurrency(L.commission.rate_diff)}` : ''} − Discounts: {formatCurrency(L.commission.discounts)}</td><td></td></tr>
              {L.zakat > 0 && <tr><td><HeadLink head="ZAKAT" testid="bs-zakat-row">Zakat Payable</HeadLink></td><td>{formatCurrency(L.zakat)}</td></tr>}
              <tr className="total-row"><td><strong>TOTAL LIABILITIES</strong></td><td><strong>{formatCurrency(L.total)}</strong></td></tr>
            </tbody>
          </table>
        </div>
        <div className="bs-section">
          <h3>ASSETS</h3>
          <table>
            <tbody>
              <tr><td><HeadLink head="CASH" testid="bs-cash-row">Cash Balance</HeadLink></td><td>{formatCurrency(A.cash_balance)}</td></tr>
              <tr><td><HeadLink head="BANK" testid="bs-bank-row">Bank Balance</HeadLink></td><td>{formatCurrency(A.bank_balance)}</td></tr>
              <tr><td><span onClick={() => navigate('/dukandar-ledger')} style={headLinkStyle} title="Go to Dukandar Ledger">Patti (Dukandar Receivable)</span></td><td>{formatCurrency(A.patti)}</td></tr>
              {A.bepaari_advances > 0 && <tr><td><span onClick={() => navigate('/bepaari-ledger')} style={headLinkStyle} title="Go to Bepaari Ledger">Bepaari Advances</span></td><td>{formatCurrency(A.bepaari_advances)}</td></tr>}
              <tr className="sub-item"><td><HeadLink head="MANDI_EXPENSE" testid="bs-mandiexp-row">Mandi Expenses</HeadLink></td><td>{formatCurrency(A.mandi_expenses.total)}</td></tr>
              <tr className="sub-item"><td><HeadLink head="BF_DISCOUNT" testid="bs-bfdisc-row">BF Discount</HeadLink></td><td>{formatCurrency(A.bf_discount.total)}</td></tr>
              <tr className="sub-item"><td><HeadLink head="MHN_PERSONAL" testid="bs-mhn-row">MHN Personal</HeadLink></td><td>{formatCurrency(A.mhn_personal.total)}</td></tr>
              {A.advance_receivables.map((adv, i) => (
                <tr key={i}>
                  <td>
                    {adv.id ? (
                      <span onClick={() => navigate(`/party-statement?type=advance&id=${adv.id}`)} style={headLinkStyle} title="View party statement">
                        {adv.name} Receivable
                      </span>
                    ) : `${adv.name} Receivable`}
                  </td>
                  <td>{formatCurrency(adv.amount)}</td>
                </tr>
              ))}
              <tr className="total-row"><td><strong>TOTAL ASSETS</strong></td><td><strong>{formatCurrency(A.total)}</strong></td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== HEAD STATEMENT (P&L / non-party head drill-down) ==============
const HEAD_LABELS = {
  MANDI_EXPENSE: "Mandi Expenses",
  BF_DISCOUNT: "BF Discount",
  MHN_PERSONAL: "MHN Personal",
  KK: "KK",
  JB: "JB",
  COMMISSION: "Commission",
  ZAKAT: "Zakat",
  CASH: "Cash",
  BANK: "Bank",
  CAPITAL: "Capital",
  AMANAT: "Amanat",
};

const HeadStatement = () => {
  const { head } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const headKey = (head || "").toUpperCase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(searchParams.get("from_date") || "");
  const [toDate, setToDate] = useState(searchParams.get("to_date") || "");
  const [subtypeFilter, setSubtypeFilter] = useState("ALL");

  const fetch = async () => {
    setLoading(true);
    let url = `${API}/head-statement/${headKey}?`;
    if (fromDate) url += `from_date=${fromDate}&`;
    if (toDate) url += `to_date=${toDate}&`;
    try {
      const res = await axios.get(url);
      setData(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to load head statement");
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, [headKey, fromDate, toDate]);

  const downloadCSV = () => {
    let url = `${API}/head-statement/${headKey}/export?`;
    if (fromDate) url += `from_date=${fromDate}&`;
    if (toDate) url += `to_date=${toDate}&`;
    window.open(url, '_blank');
  };

  const doPrint = () => window.print();

  if (!HEAD_LABELS[headKey]) {
    return (
      <div className="page">
        <h2>Unknown Head</h2>
        <p>No statement is available for "{head}".</p>
        <button onClick={() => navigate('/balance-sheet')}>← Back to Balance Sheet</button>
      </div>
    );
  }

  if (loading || !data) return <div className="loading">Loading...</div>;

  const filteredEntries = subtypeFilter === "ALL"
    ? data.entries
    : data.entries.filter(e => (e.subtype || "OTHER") === subtypeFilter);

  const isMandiExp = headKey === "MANDI_EXPENSE";
  const normalDebit = data.normal_balance === "debit";

  return (
    <div className="page" data-testid="head-statement-page">
      <div className="no-print" style={{display:'flex', gap:8, alignItems:'center', marginBottom:8}}>
        <button onClick={() => navigate('/balance-sheet')} data-testid="hs-back-btn">← Balance Sheet</button>
        <h2 style={{margin:0}}>{data.label} — Statement</h2>
      </div>

      <div className="filter-bar no-print" style={{flexWrap:'wrap', gap:8}}>
        <label>From:</label>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="hs-from-date" />
        <label>To:</label>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="hs-to-date" />
        {(fromDate || toDate) && <button className="btn-clear" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="hs-clear-dates">Clear</button>}
        <span style={{flex:1}}></span>
        <button onClick={downloadCSV} data-testid="hs-export-csv">📊 CSV</button>
        <button onClick={doPrint} data-testid="hs-print">🖨 Print / PDF</button>
      </div>

      {/* Summary chips */}
      <div className="hs-summary-cards" style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12, margin:'12px 0'}}>
        <div className="hs-card" style={{padding:12, background:'#f7fafc', border:'1px solid #e2e8f0', borderRadius:6}}>
          <div style={{fontSize:11, color:'#718096'}}>Opening Balance</div>
          <div style={{fontSize:18, fontWeight:700}} data-testid="hs-opening">{formatCurrency(data.opening_balance)}</div>
        </div>
        <div className="hs-card" style={{padding:12, background:'#f7fafc', border:'1px solid #e2e8f0', borderRadius:6}}>
          <div style={{fontSize:11, color:'#718096'}}>Total Debit</div>
          <div style={{fontSize:18, fontWeight:700, color:'#c53030'}}>{formatCurrency(data.total_debit)}</div>
        </div>
        <div className="hs-card" style={{padding:12, background:'#f7fafc', border:'1px solid #e2e8f0', borderRadius:6}}>
          <div style={{fontSize:11, color:'#718096'}}>Total Credit</div>
          <div style={{fontSize:18, fontWeight:700, color:'#2f855a'}}>{formatCurrency(data.total_credit)}</div>
        </div>
        <div className="hs-card" style={{padding:12, background:'#fefcbf', border:'1px solid #d69e2e', borderRadius:6}}>
          <div style={{fontSize:11, color:'#744210'}}>Closing Balance ({data.normal_balance === 'debit' ? 'Dr' : 'Cr'})</div>
          <div style={{fontSize:18, fontWeight:800}} data-testid="hs-closing">{formatCurrency(data.closing_balance)}</div>
        </div>
      </div>

      {/* Mandi Exp subtype summary card + filter */}
      {isMandiExp && data.subtype_summary.length > 0 && (
        <div style={{marginBottom:16, padding:12, background:'#fff', border:'1px solid #e2e8f0', borderRadius:6}} data-testid="hs-subtype-summary">
          <div style={{fontWeight:700, marginBottom:8}}>Breakdown by Subtype</div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:8}}>
            {data.subtype_summary.map(s => (
              <div key={s.subtype}
                   onClick={() => setSubtypeFilter(subtypeFilter === s.subtype ? "ALL" : s.subtype)}
                   className="no-print"
                   style={{
                     cursor:'pointer', padding:8, borderRadius:4,
                     background: subtypeFilter === s.subtype ? '#bee3f8' : '#edf2f7',
                     border: subtypeFilter === s.subtype ? '2px solid #2b6cb0' : '1px solid #cbd5e0'
                   }}
                   data-testid={`hs-subtype-chip-${s.subtype}`}>
                <div style={{fontSize:11, color:'#4a5568', fontWeight:600}}>{s.subtype}</div>
                <div style={{fontSize:14, fontWeight:700}}>{formatCurrency(normalDebit ? s.debit - s.credit : s.credit - s.debit)}</div>
                <div style={{fontSize:10, color:'#718096'}}>{s.count} entries</div>
              </div>
            ))}
          </div>
          {subtypeFilter !== "ALL" && (
            <button className="no-print" style={{marginTop:8}} onClick={() => setSubtypeFilter("ALL")} data-testid="hs-clear-subtype">Show All Subtypes</button>
          )}
        </div>
      )}

      {/* Full chronological table */}
      <div className="table-wrap">
        <table data-testid="hs-entries-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Subtype</th>
              <th>Party</th>
              <th>Mode</th>
              <th style={{textAlign:'right'}}>Debit</th>
              <th style={{textAlign:'right'}}>Credit</th>
              <th style={{textAlign:'right'}}>Balance ({data.normal_balance === 'debit' ? 'Dr' : 'Cr'})</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.length === 0 ? (
              <tr><td colSpan={8} style={{textAlign:'center', color:'#718096', padding:20}}>No entries in this range.</td></tr>
            ) : filteredEntries.map((e, i) => (
              <tr key={`${e.source}-${e.ref_id}-${i}`}>
                <td>{e.date}</td>
                <td>{e.description}</td>
                <td>{e.subtype || '—'}</td>
                <td>{e.party_name || '—'}</td>
                <td>{e.mode || '—'}</td>
                <td style={{textAlign:'right', color: e.debit ? '#c53030' : '#a0aec0'}}>{e.debit ? formatCurrency(e.debit) : '—'}</td>
                <td style={{textAlign:'right', color: e.credit ? '#2f855a' : '#a0aec0'}}>{e.credit ? formatCurrency(e.credit) : '—'}</td>
                <td style={{textAlign:'right', fontWeight:600}}>{formatCurrency(e.running_balance)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{fontWeight:700, background:'#f7fafc'}}>
              <td colSpan={5} style={{textAlign:'right'}}>Totals</td>
              <td style={{textAlign:'right', color:'#c53030'}}>{formatCurrency(data.total_debit)}</td>
              <td style={{textAlign:'right', color:'#2f855a'}}>{formatCurrency(data.total_credit)}</td>
              <td style={{textAlign:'right'}}>{formatCurrency(data.closing_balance)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ============== MASTERS ==============
const Masters = () => {
  const [bepaaris, setBeparis] = useState([]);
  const [dukandars, setDukandars] = useState([]);
  const [advParties, setAdvParties] = useState([]);
  const [capPartners, setCapPartners] = useState([]);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", opening_balance: "0", commission_percent: "4", flat_rate_per_goat: "", jb_rate_override: "", partner_type: "CAPITAL", phone: "" });
  const [activeTab, setActiveTab] = useState("bepaaris");
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchData = async () => {
    const [b, d, a, c, s] = await Promise.all([
      axios.get(`${API}/bepaaris`), axios.get(`${API}/dukandars`), axios.get(`${API}/advance-parties`),
      axios.get(`${API}/capital-partners`), axios.get(`${API}/settings`)
    ]);
    setBeparis(b.data); setDukandars(d.data); setAdvParties(a.data); setCapPartners(c.data); setSettings(s.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const endpoints = { bepaaris: "/bepaaris", dukandars: "/dukandars", advance: "/advance-parties", capital: "/capital-partners" };
    await axios.post(`${API}${endpoints[activeTab]}`, {
      name: form.name, opening_balance: parseFloat(form.opening_balance || 0),
      commission_percent: form.flat_rate_per_goat ? null : parseFloat(form.commission_percent || 4),
      flat_rate: form.flat_rate_per_goat ? parseFloat(form.flat_rate_per_goat) : null,
      jb_rate_override: form.jb_rate_override ? parseFloat(form.jb_rate_override) : null,
      partner_type: form.partner_type, phone: form.phone
    });
    setForm({ name: "", opening_balance: "0", commission_percent: "4", flat_rate_per_goat: "", jb_rate_override: "", partner_type: "CAPITAL", phone: "" });
    fetchData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    const endpoints = { bepaaris: "/bepaaris", dukandars: "/dukandars", advance: "/advance-parties", capital: "/capital-partners" };
    await axios.delete(`${API}${endpoints[activeTab]}/${id}`);
    fetchData();
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || "",
      phone: item.phone || "",
      opening_balance: item.opening_balance || 0,
      commission_percent: item.flat_rate_per_goat ? "" : (item.commission_percent || 4),
      flat_rate_per_goat: item.flat_rate_per_goat || "",
      jb_rate_override: item.jb_rate_override != null ? item.jb_rate_override : "",
      partner_type: item.partner_type || "CAPITAL"
    });
  };

  const handleEditSave = async () => {
    const endpoints = { bepaaris: "/bepaaris", dukandars: "/dukandars", advance: "/advance-parties", capital: "/capital-partners" };
    const updates = { name: editForm.name };
    
    if (activeTab === "bepaaris" || activeTab === "dukandars") {
      updates.phone = editForm.phone;
    }
    if (activeTab === "bepaaris") {
      if (editForm.flat_rate_per_goat) {
        updates.flat_rate_per_goat = parseFloat(editForm.flat_rate_per_goat);
        updates.commission_percent = null;
      } else {
        updates.commission_percent = parseFloat(editForm.commission_percent || 4);
        updates.flat_rate_per_goat = null;
      }
      updates.jb_rate_override = editForm.jb_rate_override !== "" && editForm.jb_rate_override != null
        ? parseFloat(editForm.jb_rate_override)
        : null;
    }
    updates.opening_balance = parseFloat(editForm.opening_balance);
    
    await axios.put(`${API}${endpoints[activeTab]}/${editItem.id}`, updates);
    setEditItem(null);
    fetchData();
  };

  const handleSettingsUpdate = async () => {
    await axios.put(`${API}/settings`, settings);
    alert("Settings saved!");
  };

  if (loading) return <div className="loading">Loading...</div>;

  const getActiveData = () => {
    switch (activeTab) {
      case "bepaaris": return bepaaris;
      case "dukandars": return dukandars;
      case "advance": return advParties;
      case "capital": return capPartners;
      default: return [];
    }
  };

  return (
    <div className="page">
      <h2>Masters & Settings</h2>
      
      <div className="tabs">
        {["bepaaris", "dukandars", "advance", "capital", "settings"].map((tab) => (
          <button key={tab} className={`tab ${activeTab === tab ? "active" : ""}`} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === "settings" ? (
        <div className="settings-form">
          <h3>System Settings</h3>
          <div className="settings-grid">
            <label>Default Commission %
              <input type="number" value={settings.commission_rate || ""} onChange={(e) => setSettings({ ...settings, commission_rate: parseFloat(e.target.value), default_flat_rate: null })} />
            </label>
            <label className="or-label">OR</label>
            <label>Default ₹ Per Goat
              <input type="number" value={settings.default_flat_rate || ""} onChange={(e) => setSettings({ ...settings, default_flat_rate: parseFloat(e.target.value), commission_rate: null })} />
            </label>
            <label>KK Fixed<input type="number" value={settings.kk_fixed || ""} onChange={(e) => setSettings({ ...settings, kk_fixed: parseFloat(e.target.value) })} /></label>
            <label>JB Rate<input type="number" value={settings.jb_rate || ""} onChange={(e) => setSettings({ ...settings, jb_rate: parseFloat(e.target.value) })} /></label>
            <label>Opening Cash<input type="number" value={settings.opening_cash || ""} onChange={(e) => setSettings({ ...settings, opening_cash: parseFloat(e.target.value) })} /></label>
            <label>Opening Bank<input type="number" value={settings.opening_bank || ""} onChange={(e) => setSettings({ ...settings, opening_bank: parseFloat(e.target.value) })} /></label>
            <label>JB Opening<input type="number" value={settings.jb_opening || ""} onChange={(e) => setSettings({ ...settings, jb_opening: parseFloat(e.target.value) })} /></label>
            <label>KK Opening<input type="number" value={settings.kk_opening || ""} onChange={(e) => setSettings({ ...settings, kk_opening: parseFloat(e.target.value) })} /></label>
            <label>Commission Opening<input type="number" value={settings.commission_opening || ""} onChange={(e) => setSettings({ ...settings, commission_opening: parseFloat(e.target.value) })} /></label>
            <label>Zakat Opening<input type="number" value={settings.zakat_opening || ""} onChange={(e) => setSettings({ ...settings, zakat_opening: parseFloat(e.target.value) })} /></label>
          </div>
          <p className="hint">Default commission model applies to new bepaaris. Each bepaari can override in their individual settings.</p>
          <button className="btn-primary" onClick={handleSettingsUpdate}>Save Settings</button>
          
          <div className="export-section" style={{marginTop: 32, paddingTop: 24, borderTop: '2px solid #C5A55A'}}>
            <h3>Data Export</h3>
            <p style={{color: '#475569', fontSize: 13, marginBottom: 12}}>Download all data as a single Excel file (Daily Sales, Cash & Bank, Bepaari Ledger, Dukandar Ledger, Balance Sheet, Adjustments)</p>
            <button className="btn-export" data-testid="export-all-btn" onClick={() => { window.open(`${API}/export-all`, '_blank'); }}>Download All Data (Excel)</button>
          </div>

          <UserManagement />
        </div>
      ) : (
        <>
          <form className="entry-form" onSubmit={handleAdd}>
            <input type="text" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input type="number" placeholder="Opening Balance" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
            {(activeTab === "bepaaris" || activeTab === "dukandars") && (
              <input type="text" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            )}
            {activeTab === "bepaaris" && (
              <>
                <input type="number" placeholder="Commission %" value={form.commission_percent}
                  onChange={(e) => setForm({ ...form, commission_percent: e.target.value, flat_rate_per_goat: "" })} />
                <span className="or-divider">OR</span>
                <input type="number" placeholder="₹ Per Goat" value={form.flat_rate_per_goat}
                  onChange={(e) => setForm({ ...form, flat_rate_per_goat: e.target.value, commission_percent: "" })} />
                <input type="number" placeholder="JB ₹/goat (override, optional)"
                  title="Leave blank to use global JB rate from Settings. Set a value to override JB rate for this Bepaari only (useful during Qurbani/Eid)."
                  value={form.jb_rate_override}
                  onChange={(e) => setForm({ ...form, jb_rate_override: e.target.value })}
                  data-testid="bep-jb-override"
                  style={{maxWidth: '180px'}} />
              </>
            )}
            {activeTab === "capital" && (
              <select value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value })}>
                <option value="CAPITAL">CAPITAL</option><option value="LOAN">LOAN</option><option value="AMANAT">AMANAT</option>
              </select>
            )}
            <button type="submit" className="btn-primary">Add</button>
          </form>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  {(activeTab === "bepaaris" || activeTab === "dukandars") && <th>Phone</th>}
                  <th>Opening Balance</th>
                  {activeTab === "bepaaris" && <th>Commission Model</th>}
                  {activeTab === "capital" && <th>Type</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getActiveData().map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    {(activeTab === "bepaaris" || activeTab === "dukandars") && <td>{item.phone || "-"}</td>}
                    <td>{formatCurrency(item.opening_balance)}</td>
                    {activeTab === "bepaaris" && <td>{item.flat_rate_per_goat ? `₹${item.flat_rate_per_goat}/goat` : `${item.commission_percent}%`}</td>}
                    {activeTab === "capital" && <td>{item.partner_type}</td>}
                    <td>
                      <button className="btn-edit" onClick={() => handleEdit(item)} data-testid={`edit-${item.id}`}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(item.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editItem && (
        <div className="modal-overlay" onClick={() => setEditItem(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit {activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(1, -1)}</h3>
            <div className="edit-form">
              <label>
                Name:
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </label>
              {(activeTab === "bepaaris" || activeTab === "dukandars") && (
                <label>
                  Phone:
                  <input type="text" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                </label>
              )}
              <label>
                Opening Balance:
                <input type="number" value={editForm.opening_balance} onChange={(e) => setEditForm({ ...editForm, opening_balance: e.target.value })} />
              </label>
              {activeTab === "bepaaris" && (
                <>
                  <label>
                    Commission %:
                    <input type="number" value={editForm.commission_percent}
                      onChange={(e) => setEditForm({ ...editForm, commission_percent: e.target.value, flat_rate_per_goat: "" })}
                      placeholder="e.g. 4" />
                  </label>
                  <div className="or-divider-block">— OR —</div>
                  <label>
                    Flat Rate (₹/goat):
                    <input type="number" value={editForm.flat_rate_per_goat}
                      onChange={(e) => setEditForm({ ...editForm, flat_rate_per_goat: e.target.value, commission_percent: "" })}
                      placeholder="e.g. 500" />
                  </label>
                  <label>
                    JB Override (₹/goat — leave blank for default):
                    <input type="number" value={editForm.jb_rate_override}
                      onChange={(e) => setEditForm({ ...editForm, jb_rate_override: e.target.value })}
                      placeholder="e.g. 100 during Qurbani"
                      data-testid="edit-bep-jb-override" />
                  </label>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-clear" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== ACTIVITY LOG ==============
const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState({ collection: "", action: "" });
  const [loading, setLoading] = useState(true);

  const fetchLogs = async () => {
    setLoading(true);
    let url = `${API}/activity-log?limit=200`;
    if (filter.collection) url += `&collection=${filter.collection}`;
    if (filter.action) url += `&action=${filter.action}`;
    const res = await axios.get(url);
    setLogs(res.data);
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, [filter]);

  const collectionLabels = { daily_sales: "Daily Sales", cash_book: "Cash & Bank", adjustments: "Adjustments", balance_transfers: "Balance Transfer" };
  const actionColors = { CREATE: "#166534", EDIT: "#C5A55A", DELETE: "#991B1B" };

  return (
    <div className="page">
      <h2>Activity Log</h2>
      <p className="hint">Audit trail of all creates, edits and deletes across the system</p>

      <div className="filter-bar">
        <select value={filter.collection} onChange={(e) => setFilter({ ...filter, collection: e.target.value })}>
          <option value="">All Collections</option>
          <option value="daily_sales">Daily Sales</option>
          <option value="cash_book">Cash & Bank</option>
          <option value="adjustments">Adjustments</option>
          <option value="balance_transfers">Balance Transfers</option>
        </select>
        <select value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })}>
          <option value="">All Actions</option>
          <option value="CREATE">Creates Only</option>
          <option value="EDIT">Edits Only</option>
          <option value="DELETE">Deletes Only</option>
        </select>
        <button className="btn-clear" onClick={() => setFilter({ collection: "", action: "" })}>Clear</button>
        <span style={{ color: '#475569', fontSize: 13 }}>{logs.length} entries</span>
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <div className="activity-log-list">
          {logs.length === 0 && <div className="no-data">No activity logged yet. Creates, edits and deletes will appear here.</div>}
          {logs.map((log) => (
            <div key={log.id} className="log-card" data-testid="log-entry">
              <div className="log-header">
                <div className="log-meta">
                  <span className="log-action-badge" style={{ background: actionColors[log.action] || '#475569' }}>{log.action}</span>
                  <span className="log-collection">{collectionLabels[log.collection] || log.collection}</span>
                  <span className="log-user">by {log.user}</span>
                </div>
                <span className="log-time">{new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              {log.summary && <div className="log-summary">{log.summary}</div>}
              {log.changes && log.changes.length > 0 && (
                <div className="log-changes">
                  {log.changes.map((c, i) => (
                    <div key={i} className="log-change-row">
                      <span className="log-field">{c.field}</span>
                      {log.action === "EDIT" ? (
                        <>
                          <span className="log-old">{c.old || '(empty)'}</span>
                          <span className="log-arrow">&rarr;</span>
                          <span className="log-new">{c.new || '(empty)'}</span>
                        </>
                      ) : log.action === "CREATE" ? (
                        <span className="log-new">{c.new}</span>
                      ) : (
                        <span className="log-old">{c.old}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== USER MANAGEMENT ==============
const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });
  const [msg, setMsg] = useState("");

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) { }
  };

  useEffect(() => { if (currentUser?.role === 'admin') fetchUsers(); }, [currentUser]);

  if (currentUser?.role !== 'admin') return null;

  const handleAdd = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await axios.post(`${API}/users`, form);
      setForm({ email: "", password: "", name: "", role: "user" });
      setMsg("User created");
      fetchUsers();
    } catch (err) {
      setMsg(err.response?.data?.detail || "Error creating user");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="user-mgmt" style={{marginTop: 32, paddingTop: 24, borderTop: '2px solid #C5A55A'}}>
      <h3>User Management</h3>
      <p className="hint">Create and manage user accounts</p>
      {msg && <div className="success-message">{msg}</div>}
      <form className="entry-form" onSubmit={handleAdd} style={{marginBottom: 16}}>
        <input type="text" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        <button className="btn-primary" type="submit">Add User</button>
      </form>
      <table>
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Actions</th></tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u._id}>
              <td>{u.name}</td>
              <td>{u.email}</td>
              <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
              <td>{u._id !== currentUser._id && <button className="btn-delete" onClick={() => handleDelete(u._id)}>Delete</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============== DEMO WELCOME TOUR (Spotlight) ==============
const DemoTour = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [show, setShow] = useState(false);
  const [rect, setRect] = useState(null);
  const [tick, setTick] = useState(0); // re-measure trigger

  useEffect(() => {
    if (user?.tenant === 'demo' && !localStorage.getItem('demo_tour_seen_v1')) {
      const t = setTimeout(() => setShow(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  // Tour steps: each step targets a specific UI element by selector (or null = centered modal)
  const steps = [
    {
      icon: '👋',
      title: 'Welcome to Mandipro Demo!',
      body: "This is your private sandbox. Whatever you enter stays isolated from real data. Take 60 seconds to see how it works.",
      target: null,
    },
    {
      icon: '⚙️',
      title: 'Step 1 — Add Masters',
      body: "Click here to add your Bepaaris (sellers), Dukandars (buyers), Capital partners, and Loan parties. The foundation of every entry.",
      target: '[data-testid="nav-masters"]',
    },
    {
      icon: '🐐',
      title: 'Step 2 — Daily Sales',
      body: "This is where you log every sale. Pick the Bepaari, Dukandar, qty, rates — Mandipro auto-calculates commission, mandi exp, and net amounts.",
      target: '[data-testid="nav-daily-sales"]',
    },
    {
      icon: '💰',
      title: 'Step 3 — Cash & Bank',
      body: "Record receipts from Dukandars and payments to Bepaaris here. Pick mode (Cash / Bank / UPI) — every entry auto-links to ledgers.",
      target: '[data-testid="nav-cash-book"]',
    },
    {
      icon: '🔄',
      title: 'Step 4 — Adjustments (JV)',
      body: "Triangular settlements made easy. e.g. 'Bepaari Ali pays Dukandar Vinod' — one entry hits both ledgers correctly.",
      target: '[data-testid="nav-adjustments"]',
    },
    {
      icon: '📑',
      title: 'Step 5 — Balance Sheet',
      body: "The proof. Every entry you make keeps Assets = Liabilities + Capital, automatically. Try to break it — you can't!",
      target: '[data-testid="nav-balance-sheet"]',
    },
    {
      icon: '🧾',
      title: 'Step 6 — Bepaari Aakda',
      body: "Daily settlement slips. Print a clean summary of each Bepaari's transactions for the day. Ready to share over WhatsApp.",
      target: '[data-testid="nav-bepaari-aakda"]',
    },
    {
      icon: '↻',
      title: 'Step 7 — Reset Anytime',
      body: "See the yellow banner up top? Hit 'Reset Demo Data' for a clean slate. Real production data is never touched.",
      target: '[data-testid="demo-reset-btn"]',
    },
    {
      icon: '🚀',
      title: "You're ready!",
      body: "Start with Masters → Daily Sales → Balance Sheet. Have fun! Loved it? Get in touch to set up your own private workspace.",
      target: null,
    },
  ];

  // Measure target element on each step / resize / scroll
  useEffect(() => {
    if (!show) return;
    const measure = () => {
      const sel = steps[step]?.target;
      if (!sel) {
        setRect(null);
        return;
      }
      const el = document.querySelector(sel);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    measure();
    const onResize = () => { setTick(t => t + 1); };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [step, show, tick]);

  if (!show) return null;

  const close = () => {
    localStorage.setItem('demo_tour_seen_v1', '1');
    setShow(false);
  };
  const next = () => {
    if (step >= steps.length - 1) close();
    else setStep(step + 1);
  };
  const prev = () => { if (step > 0) setStep(step - 1); };

  const cur = steps[step];
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;
  const PAD = 8;       // padding around highlight
  const CARD_W = 360;
  const CARD_H_EST = 230;

  // Compute card position: prefer right of target, fallback below, fallback centered
  let cardStyle = {
    position: 'fixed',
    zIndex: 10001,
    width: `${CARD_W}px`,
    maxWidth: 'calc(100vw - 32px)',
  };
  if (!rect) {
    cardStyle.top = '50%';
    cardStyle.left = '50%';
    cardStyle.transform = 'translate(-50%, -50%)';
  } else {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rightSpace = vw - (rect.left + rect.width);
    const belowSpace = vh - (rect.top + rect.height);
    if (rightSpace >= CARD_W + 24) {
      // place to the right
      cardStyle.left = `${rect.left + rect.width + 16}px`;
      cardStyle.top = `${Math.max(16, Math.min(rect.top, vh - CARD_H_EST - 16))}px`;
    } else if (belowSpace >= CARD_H_EST + 16) {
      // place below
      cardStyle.left = `${Math.max(16, Math.min(rect.left, vw - CARD_W - 16))}px`;
      cardStyle.top = `${rect.top + rect.height + 14}px`;
    } else {
      // place above
      cardStyle.left = `${Math.max(16, Math.min(rect.left, vw - CARD_W - 16))}px`;
      cardStyle.top = `${Math.max(16, rect.top - CARD_H_EST - 14)}px`;
    }
  }

  // Mask: 4 dark rectangles around the spotlight (no blur)
  const overlays = [];
  if (rect) {
    const t = rect.top - PAD;
    const l = rect.left - PAD;
    const w = rect.width + PAD * 2;
    const h = rect.height + PAD * 2;
    const dark = 'rgba(15, 23, 42, 0.68)';
    // top
    overlays.push(<div key="t" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: `${Math.max(0, t)}px`, background: dark, zIndex: 10000, pointerEvents: 'auto' }} />);
    // bottom
    overlays.push(<div key="b" style={{ position: 'fixed', top: `${t + h}px`, left: 0, width: '100vw', height: `calc(100vh - ${t + h}px)`, background: dark, zIndex: 10000, pointerEvents: 'auto' }} />);
    // left
    overlays.push(<div key="l" style={{ position: 'fixed', top: `${t}px`, left: 0, width: `${Math.max(0, l)}px`, height: `${h}px`, background: dark, zIndex: 10000, pointerEvents: 'auto' }} />);
    // right
    overlays.push(<div key="r" style={{ position: 'fixed', top: `${t}px`, left: `${l + w}px`, width: `calc(100vw - ${l + w}px)`, height: `${h}px`, background: dark, zIndex: 10000, pointerEvents: 'auto' }} />);
    // ring around target (no blur, just a glowing outline)
    overlays.push(<div key="ring" style={{
      position: 'fixed',
      top: `${t}px`,
      left: `${l}px`,
      width: `${w}px`,
      height: `${h}px`,
      border: '3px solid #fbbf24',
      borderRadius: '10px',
      boxShadow: '0 0 0 4px rgba(251, 191, 36, 0.25), 0 0 24px rgba(251, 191, 36, 0.55)',
      zIndex: 10000,
      pointerEvents: 'none',
      animation: 'pulseRing 1.6s ease-in-out infinite',
    }} />);
  } else {
    // full-screen dim for centered welcome / final step
    overlays.push(<div key="full" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.7)', zIndex: 10000 }} />);
  }

  return (
    <>
      <style>{`@keyframes pulseRing {0%,100%{box-shadow:0 0 0 4px rgba(251,191,36,0.25),0 0 24px rgba(251,191,36,0.55)}50%{box-shadow:0 0 0 8px rgba(251,191,36,0.18),0 0 36px rgba(251,191,36,0.75)}}`}</style>
      {overlays}
      <div data-testid="demo-tour-card" style={{
        ...cardStyle,
        background: '#fff',
        borderRadius: '14px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
        padding: '22px 22px 18px',
        border: '2px solid #fbbf24',
      }}>
        <button
          data-testid="demo-tour-skip"
          onClick={close}
          style={{
            position: 'absolute',
            top: '8px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '12px',
            color: '#94a3b8',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >Skip ✕</button>
        <div style={{ fontSize: '34px', marginBottom: '4px' }}>{cur.icon}</div>
        <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#1f2937', margin: '0 0 8px' }}>{cur.title}</h3>
        <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.55, margin: '0 0 16px' }}>{cur.body}</p>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {steps.map((_, i) => (
            <span key={i} style={{
              width: i === step ? '20px' : '6px',
              height: '6px',
              borderRadius: '999px',
              background: i === step ? '#f59e0b' : '#e2e8f0',
              transition: 'all 0.25s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!isFirst && (
            <button
              data-testid="demo-tour-back"
              onClick={prev}
              style={{
                flex: '0 0 auto',
                background: '#f1f5f9',
                color: '#475569',
                border: 'none',
                padding: '10px 14px',
                borderRadius: '7px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >← Back</button>
          )}
          <button
            data-testid="demo-tour-next"
            onClick={next}
            style={{
              flex: 1,
              background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
              color: '#fff',
              border: 'none',
              padding: '10px',
              borderRadius: '7px',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.3px',
            }}
          >{isLast ? "Let's go 🚀" : `Next → ${step + 1} / ${steps.length}`}</button>
        </div>
      </div>
    </>
  );
};

// ============== DEMO BANNER ==============
const DemoBanner = () => {
  const { user } = useAuth();
  const [resetting, setResetting] = useState(false);
  if (user?.tenant !== 'demo') return null;

  const handleReset = async () => {
    if (!window.confirm('Reset demo data?\n\nThis will wipe ALL test entries (sales, cash book, JVs, masters) so the next prospect starts fresh. Your real production data is NOT touched.')) return;
    setResetting(true);
    try {
      await axios.post(`${API}/demo/reset`);
      window.location.reload();
    } catch (err) {
      alert('Reset failed: ' + (err.response?.data?.detail || err.message));
      setResetting(false);
    }
  };

  const replayTour = () => {
    localStorage.removeItem('demo_tour_seen_v1');
    window.location.reload();
  };

  return (
    <div data-testid="demo-banner" style={{
      background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
      color: '#1f2937',
      padding: '10px 16px',
      fontWeight: 600,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      flexWrap: 'wrap',
      borderBottom: '2px solid #d97706',
      boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
    }}>
      <span>🧪 DEMO MODE — your test entries are private and isolated. Real production data is untouched.</span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          data-testid="demo-replay-tour-btn"
          onClick={replayTour}
          style={{
            background: 'transparent',
            color: '#1f2937',
            border: '1px solid #1f2937',
            padding: '6px 12px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600
          }}
        >👋 Replay Tour</button>
        <button
          data-testid="demo-reset-btn"
          onClick={handleReset}
          disabled={resetting}
          style={{
            background: '#1f2937',
            color: '#fbbf24',
            border: 'none',
            padding: '6px 14px',
            borderRadius: '6px',
            cursor: resetting ? 'wait' : 'pointer',
            fontSize: '13px',
            fontWeight: 600
          }}
        >{resetting ? 'Resetting…' : '↻ Reset Demo Data'}</button>
      </div>
    </div>
  );
};

// ============== MAIN APP ==============
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <div className="app-container">
                <Sidebar />
                <main className="main-content">
                  <DemoBanner />
                  <DemoTour />
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/daily-sales" element={<DailySales />} />
                    <Route path="/cash-book" element={<CashBook />} />
                    <Route path="/adjustments" element={<Adjustments />} />
                    <Route path="/balance-transfer" element={<BalanceTransfer />} />
                    <Route path="/bepaari-ledger" element={<BepariLedger />} />
                    <Route path="/dukandar-ledger" element={<DukandarLedger />} />
                    <Route path="/balance-sheet" element={<AdminRoute><BalanceSheet /></AdminRoute>} />
                    <Route path="/head-statement/:head" element={<AdminRoute><HeadStatement /></AdminRoute>} />
                    <Route path="/bepaari-aakda" element={<BepariAakda />} />
                    <Route path="/party-statement" element={<PartyStatement />} />
                    <Route path="/masters" element={<AdminRoute><Masters /></AdminRoute>} />
                    <Route path="/activity-log" element={<AdminRoute><ActivityLog /></AdminRoute>} />
                  </Routes>
                </main>
              </div>
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
